import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import {
  Sparkles, FileText, Download, ExternalLink, Lock,
  Loader2, FileSpreadsheet, Presentation, BookOpen,
  ChevronRight, ChevronLeft, X, AlertTriangle, Info, Check,
} from 'lucide-react';
import DefinitionInfo from '../components/DefinitionInfo';
import ExecutiveSummaryModal from '../components/ExecutiveSummaryModal';
import ScoringRubricModal from '../components/ScoringRubricModal';
import ClientStatusPipeline from '../components/ClientStatusPipeline';
import { ScreeningPanel } from '@/components/ScreeningPanel';
import WhyRecommendationInfo from '../components/WhyRecommendationInfo';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ViatrisJourneyPanel } from '../components/ViatrisJourneyPanel';
import {
  CitedText,
  SectionRefStrip,
  ReferenceRail,
  CompanyProfileBlock,
  CompetitionBlock,
  GenericSection,
  CitationModeContext,
  CITATION_MODES,
} from '@/lib/deepDiveRender';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Scoring state palette — drives the sticky kanban tiles and the
// scoring progress chart. With the Jun 2026 pivot away from manual
// decision-making, the lifecycle is purely scoring-driven:
//   Pending      → 0/6 reviewers have scored.
//   Under Review → 1-5/6 have scored.
//   Scored       → all 6 reviewers have submitted scores.
const DECISION_STATES = [
  { key: 'Pending',      color: 'rose',    bar: 'bg-rose-500',    soft: 'bg-rose-50 text-rose-800 border-rose-200',         desc: 'No committee member has scored this asset yet' },
  { key: 'Under Review', color: 'amber',   bar: 'bg-amber-500',   soft: 'bg-amber-50 text-amber-800 border-amber-200',      desc: '1–5 of 6 reviewers have scored — in progress' },
  { key: 'Scored',       color: 'emerald', bar: 'bg-emerald-500', soft: 'bg-emerald-50 text-emerald-800 border-emerald-200', desc: 'All 6 reviewers have submitted scores — ready for consolidation' },
];

const DECISION_BADGE = {
  Scored:         'bg-emerald-100 text-emerald-800',
  'Under Review': 'bg-amber-100 text-amber-800',
  Pending:        'bg-rose-100 text-rose-800',
  // Legacy aliases kept so historical rows still render with a colour.
  Approved:       'bg-emerald-100 text-emerald-800',
  Rejected:       'bg-emerald-100 text-emerald-800',
};

// Tier palette — mirrors the internal CRM so the committee sees the
// same vocabulary the analyst team uses.
const TIER_BADGE = {
  'Strategic Priority': 'bg-violet-100 text-violet-800 border-violet-300',
  'High Priority':      'bg-emerald-100 text-emerald-800 border-emerald-300',
  'Medium':             'bg-sky-100 text-sky-800 border-sky-300',
  'Monitor':            'bg-amber-100 text-amber-800 border-amber-300',
  'Low Priority':       'bg-orange-100 text-orange-800 border-orange-300',
  'Not Suitable':       'bg-rose-100 text-rose-900 border-rose-400',
  'Could Not Assess':   'bg-slate-100 text-slate-700 border-slate-300',
};

// Per-tier explanations — used as title-attribute tooltips on every
// tier badge so the committee never has to ask "what does Medium mean?".
const TIER_EXPLAINER = {
  'Strategic Priority': 'Top tier — must-do partnering target, all dimensions aligned. Weighted avg ≥ 4.5 / 5. Pursue immediately.',
  'High Priority':      'Strong tier — solid on most dimensions, low risk, fast path to value. Weighted avg 3.75 – 4.5 / 5.',
  'Medium':             'Mid tier — solid but with 1-2 noteworthy gaps. Weighted avg 3.0 – 3.75 / 5. Proceed with watchlist.',
  'Monitor':            'Watch tier — interesting but needs more data. Weighted avg 2.0 – 3.0 / 5. Revisit next cycle.',
  'Low Priority':       'Defer tier — weak across dimensions today. Weighted avg < 2.0 / 5. Defer until next milestone.',
  'Not Suitable':       'Override — deal-killer risks (IP, safety, crowded, mis-fit). Rejected by AI or operator.',
  'Could Not Assess':   'AI could not produce a valid composite score (LLM returned malformed scoring data or asset missing critical fields).',
};

// Weighted-average explainer — shown as a hover tooltip on every score
// badge AND in the Scoring Methodology block on the dashboard.
// (Feb 2026) Switched from Composite /45 → Weighted Avg /5 as the primary
// display metric so partial scoring (reviewer only rates their dimension)
// doesn't understate an asset.
const SCORING_EXPLAINER =
  'Scientific Evaluation = weighted average per scored dimension (each 1–5) across 9 AI-assessed domains: ' +
  'prevalence, unmet need, efficacy, safety, competitive differentiation, time to launch, ' +
  'strategic alignment, remaining investment required, clinical adoption potential. ' +
  'The weighted average maps to a tier: ≥ 4.5 → Strategic Priority · 3.75–4.5 → High Priority · ' +
  '3.0–3.75 → Medium · 2.0–3.0 → Monitor · < 2.0 → Low Priority. ' +
  'Verdict fires off the committee weighted average / 5 once all 6 reviewers have submitted at least one score: ≥ 4.0 In-License · 3.0–3.99 Reassess · < 3.0 Decline.';

// ────────────────────────────────────────────────────────────────────
// Viatris Fit Screen (Feb 2026 MoM finalised sequence):
//   1. Backend Phase-3+ pre-filter (off-phase → auto-Not-Suitable
//      without any LLM spend). We hide these from the client portal
//      entirely — nothing was scored, so there's nothing to show.
//   2. Backend runs Perplexity + Claude on every Phase 3+ asset.
//   3. Backend TA post-filter (off-TA → tier overridden to Not Suitable
//      with off_strategy_reason so the committee can see the science-
//      based verdict AND the reason it was marked off-strategy).
// The client portal shows every Phase 3+ asset; off-TA ones surface
// under Not Suitable with a red "Off-strategy" reason chip.
// ────────────────────────────────────────────────────────────────────
const VIATRIS_PRIORITY_TA_LABEL = 'Cardiovascular · Metabolism · CNS · Immunology · Dermatology · Pain'; // eslint-disable-line no-unused-vars

// Recompute counts to reflect the auto-scoped (Phase-filtered) queue
// so every summary tile, kanban count, and cohort overview paragraph
// stays in sync with what the committee actually sees on screen.
// (Off-TA assets are NOT hidden — they surface under Not Suitable
// with an off_strategy_reason chip after Perplexity/Claude scoring.)
function applyViatrisScope(queue) {
  if (!queue) return queue;
  // Two-gate client-visible scope (Feb 2026 · parity with Admin's
  // "Viatris scope · CV / Metab / CNS / Immuno / Derm / Pain · Phase 3+"
  // button ON): asset must be BOTH Phase 3+ AND in one of the 6
  // priority TAs. Off-TA rows are dropped entirely from the client
  // portal so the tier chip counts match the Admin Recommendation
  // Distribution tiles exactly. (The Download Excel link still points
  // at the full anonymised universe workbook so nothing is *lost* —
  // it's just not surfaced in the visible tables.)
  // Jun 2026 — no client-side re-filtering. The backend review-queue is
  // already the reconciled universe (same eligible set + live tiers as
  // the Admin Workbench), so we only recompute derived-status counts.
  const active = queue.active_review || queue.assets || [];
  const reference = queue.reference_library || [];
  const by_tier_active = {};
  for (const x of active) {
    const t = x.recommendation || 'Unknown';
    by_tier_active[t] = (by_tier_active[t] || 0) + 1;
  }
  const by_tier_reference = {};
  for (const x of reference) {
    const t = x.recommendation || 'Unknown';
    by_tier_reference[t] = (by_tier_reference[t] || 0) + 1;
  }
  const counts = {
    active_total:      active.length,
    pending:           active.filter((x) => (x.derived_status || 'Pending') === 'Pending').length,
    under_review:      active.filter((x) => x.derived_status === 'Under Review').length,
    // "Scored" merges the legacy Approved/Rejected derived states — we
    // no longer surface a pass/fail verdict to the committee while
    // they're mid-cohort; that determination happens later on the
    // Workbench during consolidation.
    scored:            active.filter((x) => x.derived_status === 'Approved' || x.derived_status === 'Rejected').length,
    // Legacy aliases retained so existing readers (kanban, cohort
    // overview, nav badge) keep working without code-path changes.
    shortlisted:       active.filter((x) => x.derived_status === 'Under Review').length,
    approved:          active.filter((x) => x.derived_status === 'Approved').length,
    rejected:          active.filter((x) => x.derived_status === 'Rejected').length,
    reference_total:   reference.length,
    // cohort_total reflects the scoped universe (TA + Phase) so the
    // "X assets analyzed" headline in CohortOverviewCard matches the
    // numbers the committee can actually see/decide on.
    cohort_total:      active.length + reference.length,
    by_tier_active,
    by_tier_reference,
  };
  return {
    ...queue,
    active_review: active,
    reference_library: reference,
    assets: active,
    counts,
  };
}

export default function ViatrisClientDashboard() {
  const { token } = useParams();
  const [overview, setOverview] = useState(null);
  const [reportsLib, setReportsLib] = useState({ files: [], count: 0 });
  const [priorityUpgrades, setPriorityUpgrades] = useState({ upgrades: [], has_prior: false });
  // Lifted: shared between the sticky kanban strip and the ReviewQueueSection
  // so clicking a tile up top filters the cards below.
  const [statusFilter, setStatusFilter] = useState(null);
  // Committee Review Queue — assets that internal analysts have promoted
  // to lifecycle stage "Recommended" for the client to decide on.
  const [reviewQueue, setReviewQueue] = useState({ counts: {}, assets: [], cohort_name: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Selected cohort — drives all per-cohort data (review queue, files,
  // priority upgrades, decision progress). null/empty means "active
  // cohort" as resolved by the backend.
  const [selectedCohort, setSelectedCohort] = useState('');
  // Cohorts list — surfaces the persistent header switcher visible on
  // every tab (Home, Committee Review, Cohort Results, Reports Library)
  // so the committee can flip between monthly cohorts in one click.
  const [cohortsList, setCohortsList] = useState({ cohorts: [], default: '' });
  // Status Pipeline — 5-stage narrowing funnel (Feb 2026 parity with
  // Admin). `pipelineStage` filters the Committee Review section
  // below when the client clicks a stage tile.
  const [pipelineData, setPipelineData] = useState({ counts: {}, cohort_name: '' });
  const [pipelineStage, setPipelineStage] = useState(null);
  // Jun 2026 · the portal is split into real pages (user request).
  // Tab driven by the URL hash so links stay shareable:
  //   #home · #review-queue · #cohort-results · #cohort-reports
  const VALID_TABS = ['home', 'review-queue', 'cohort-results', 'cohort-reports'];
  const readTab = () => {
    const h = (window.location.hash || '#home').slice(1);
    return VALID_TABS.includes(h) ? h : 'home';
  };
  const [activeTab, setActiveTab] = useState(readTab);
  useEffect(() => {
    const onHash = () => setActiveTab(readTab());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resolve the currently-selected cohort's metadata (locked state etc.)
  // so the rest of the page can render lock-aware UI without needing
  // separate fetches.
  const activeCohortMeta = (cohortsList.cohorts || []).find(
    (c) => c.name === (selectedCohort || scopedQueue?.cohort_name)
  ) || null;
  const cohortLocked = !!activeCohortMeta?.locked;

  // Fetch the cohort list once at mount (cheap — small docs only).
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/client/viatris/${token}/cohorts`)
      .then((r) => {
        setCohortsList(r.data || { cohorts: [], default: '' });
        if (!selectedCohort && r.data?.default) {
          setSelectedCohort(r.data.default);
        }
      })
      .catch(() => { /* silent — header simply hides switcher */ });
    // intentionally only on token change; selectedCohort handled inline
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const q = selectedCohort ? `?cohort=${encodeURIComponent(selectedCohort)}` : '';
      const [ov, rl, rq, pu, pl] = await Promise.all([
        axios.get(`${API}/client/viatris/${token}/overview${q}`),
        axios.get(`${API}/client/viatris/${token}/cohort-files${q}`),
        axios.get(`${API}/client/viatris/${token}/review-queue${q}`),
        axios.get(`${API}/client/viatris/${token}/priority-upgrades${q}`),
        axios.get(`${API}/client/viatris/${token}/pipeline${q}`),
      ]);
      setOverview(ov.data);
      setReportsLib(rl.data || { files: [], count: 0 });
      setReviewQueue(rq.data || { counts: {}, assets: [], cohort_name: '' });
      setPriorityUpgrades(pu.data || { upgrades: [], has_prior: false });
      setPipelineData(pl.data || { counts: {}, cohort_name: '' });
    } catch (e) {
      setError(e?.response?.data?.detail || 'Invalid share link or dashboard unavailable');
    } finally {
      setLoading(false);
    }
  }, [token, selectedCohort]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Auto-apply Viatris-declared scope (Priority TAs + Phase 3+) on the
  // client side. Recomputes counts so every summary tile, kanban
  // count, and overview paragraph reflects what the committee
  // actually sees — internal Workbench is unaffected.
  const scopedQueue = React.useMemo(() => applyViatrisScope(reviewQueue), [reviewQueue]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <Lock className="w-12 h-12 mx-auto text-slate-400 mb-3" />
            <h2 className="text-xl font-bold text-slate-800 mb-2">Access denied</h2>
            <p className="text-sm text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30" data-testid="client-dashboard">
      {/* Header */}
      <header className="bg-slate-900 text-white border-b border-slate-800 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-indigo-300 mb-0.5 flex items-center gap-2">
              <Sparkles className="w-3 h-3" /> Powered by the ForesightRx Intelligence Platform (FIP)
            </div>
            <h1 className="text-2xl font-bold">Viatris Intelligence Portal</h1>
            <div className="text-[10px] text-slate-400">Search, Evaluation &amp; Licensing Intelligence</div>
          </div>
          <div className="text-right text-[11px] text-slate-300 space-y-1">
            <div>Reporting period · <strong>{overview?.reporting_period}</strong></div>
            <div>Continuously updated · {(overview?.last_intelligence_refresh || '').slice(0, 10)}</div>
            <div className="flex items-center gap-2 justify-end mt-1">
              <Badge className="bg-violet-500/20 text-violet-200 border-violet-500/30 text-[10px]">
                <Sparkles className="w-3 h-3 mr-1 inline" /> Committee Review Portal
              </Badge>
              {/* Jul 2026 · cohort switcher REMOVED — the engagement now
                  runs universe-wide (client directive: no cohorts). The
                  header shows the universe scope instead. */}
              <div className="flex items-center gap-1.5 bg-slate-800/80 border border-slate-700 rounded px-2 py-1">
                <span className="text-[9px] uppercase tracking-wide text-slate-400">Scope:</span>
                <span className="text-[11px] text-white" data-testid="header-universe-scope">China Universe</span>
              </div>
            </div>
          </div>
        </div>
        {/* Client Nav */}
        <nav className="bg-slate-800/80 border-t border-slate-700">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center gap-1 overflow-x-auto text-[11px] py-2" data-testid="client-nav">
              {[
                { hash: 'home', label: 'Home' },
                { hash: 'review-queue', label: 'Committee Review', badge: scopedQueue?.counts?.pending || 0 },
                { hash: 'cohort-results', label: 'Final Scoring' },
                { hash: 'cohort-reports', label: 'Reports' },
                { hash: 'reference-library', label: 'Reference Library', external: `/client/viatris/${token}/reference` },
              ].map((item) => (
                <a key={item.hash}
                   href={item.external ? item.external : `#${item.hash}`}
                   className={`px-3 py-1.5 rounded transition-colors whitespace-nowrap inline-flex items-center gap-1.5 ${
                     !item.external && activeTab === item.hash
                       ? 'bg-indigo-600 text-white font-semibold'
                       : 'text-slate-300 hover:bg-slate-700/70 hover:text-white'
                   }`}
                   data-testid={`nav-${item.hash}`}>
                  {item.label}
                  {item.badge > 0 && (
                    <span className="bg-rose-500 text-white text-[9px] font-bold rounded-full px-1.5 py-0.5 leading-none">
                      {item.badge}
                    </span>
                  )}
                </a>
              ))}
            </div>
          </div>
        </nav>

        {/* Sticky Kanban Strip — only on the Committee Review page,
            where clicking a tile filters the Review Queue below. */}
        {activeTab === 'review-queue' && (
          <StickyKanban
            counts={scopedQueue?.counts || {}}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
          />
        )}
      </header>

      <main className="max-w-7xl mx-auto px-6 py-5 space-y-6">
        {/* ── Page 1 · Overview — Screening Rules · Screening Funnel ·
               Reporting Cycle · Engagement Overview ──────────────── */}
        {activeTab === 'home' && (
          <>
            {/* Aug 2026 · Persistent 5-stage cohort journey (read-only) */}
            <ViatrisJourneyPanel mode="client" token={token} />
            <section id="home" className="scroll-mt-32">
              <ScreeningPanel engagementId="eng-viatris-china" cohort={selectedCohort} compact editable={false} clientToken={token} />
            </section>

            <ClientStatusPipeline
              counts={pipelineData?.counts || {}}
              stages={pipelineData?.stages || []}
              cohortName={pipelineData?.cohort_name || selectedCohort}
              activeStage={pipelineStage}
              onStageClick={(k) => setPipelineStage(k)}
            />

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-4 items-stretch">
              {(overview?.cohort_rollup || []).length > 0 && (
                <CohortStrip
                  cohorts={overview.cohort_rollup}
                  activeCohortName={overview.cohort_name}
                  selectedCohort={selectedCohort}
                  setSelectedCohort={setSelectedCohort}
                  pipelineCounts={pipelineData?.counts || {}}
                />
              )}
              <CohortOverviewCard
                counts={scopedQueue?.counts || {}}
                cohortName={scopedQueue?.cohort_name}
                universeTotal={pipelineData?.counts?.universe || 0}
              />
            </div>
          </>
        )}

        {/* Lock banner — shown on every page so reviewers know writes
            are blocked before they try to score. */}
        {cohortLocked && (
          <div
            className="bg-rose-50 border border-rose-200 text-rose-900 rounded-lg px-4 py-3 flex items-start gap-3"
            data-testid="cohort-locked-banner"
          >
            <span className="text-lg leading-none">🔒</span>
            <div className="text-xs">
              <strong>This cohort is locked for board review.</strong> You can browse the
              scoring matrix, executive summary, and reports, but scores can&apos;t be
              submitted, revised, or deleted. Contact the ForesightRx analyst team to
              re-open.
            </div>
          </div>
        )}

        {/* ── Page 2 · Committee Review — Committee Action Required ·
               Review Queue · Reference Library link ───────────────── */}
        {activeTab === 'review-queue' && (
          <ReviewQueueSection
            token={token}
            queue={scopedQueue}
            onDecisionChange={loadAll}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            cohortLocked={cohortLocked}
            pipelineStage={pipelineStage}
            setPipelineStage={setPipelineStage}
          />
        )}

        {/* ── Page 3 · Final Scoring & Recommendations (matrix) ────── */}
        {activeTab === 'cohort-results' && (
          <CohortResultsSection
            token={token}
            activeCohort={selectedCohort || scopedQueue?.cohort_name}
            cohortsList={overview?.cohort_rollup || []}
            slot="matrix"
          />
        )}

        {/* ── Page 4 · Reports — Excel / PPTX / PDF deliverables ───── */}
        {activeTab === 'cohort-reports' && (
          <CohortResultsSection
            token={token}
            activeCohort={selectedCohort || scopedQueue?.cohort_name}
            cohortsList={overview?.cohort_rollup || []}
            slot="reports"
          />
        )}

        {/* Page 5 · Reference Library lives on its own route
            (/client/viatris/{token}/reference) — linked from the nav. */}

        <footer className="text-center text-[10px] text-slate-400 py-6 border-t">
          <Lock className="w-3 h-3 inline mr-1" />
          Committee review portal · all asset evaluations and reports are produced by ForesightRx analysts. Your committee decisions sync to the ForesightRx team in real time.
        </footer>
      </main>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────
// Inline Scoring Progress — compact 3-state lifecycle strip that
// lives directly inside the Review Queue header (no separate section).
// Tiles are clickable filters; clicking again clears the filter.
// States: Pending → Under Review → Scored (all 6 reviewers in).
// ────────────────────────────────────────────────────────────────────
function InlineScoringProgress({ counts, total, activeFilter, onFilter }) {
  const c = {
    Pending:        counts.pending || 0,
    'Under Review': counts.under_review || counts.shortlisted || 0,
    Scored:         counts.scored ?? ((counts.approved || 0) + (counts.rejected || 0)),
  };
  const denom = total || (c.Pending + c['Under Review'] + c.Scored);
  if (denom === 0) return null;
  return (
    <div
      className="mb-4 pb-4 border-b border-slate-200/60"
      data-testid="inline-scoring-progress"
    >
      <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
        <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">
          Scoring Progress
        </div>
        <div className="text-[11px] text-slate-600">
          <strong className="text-slate-800">{c.Scored}</strong> of <strong className="text-slate-800">{denom}</strong> fully scored
        </div>
      </div>
      <div className="flex h-2 w-full rounded-full overflow-hidden bg-slate-100 mb-2">
        {DECISION_STATES.map((s) => {
          const n = c[s.key] || 0;
          const pct = denom > 0 ? (n / denom) * 100 : 0;
          if (pct === 0) return null;
          return (
            <div
              key={s.key}
              className={s.bar}
              style={{ width: `${pct}%` }}
              title={`${s.key}: ${n} (${pct.toFixed(0)}%)`}
              data-testid={`scoring-bar-${s.key.toLowerCase().replace(/\s+/g, '-')}`}
            />
          );
        })}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {DECISION_STATES.map((s) => {
          const n = c[s.key] || 0;
          const isActive = activeFilter === s.key;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => onFilter && onFilter(isActive ? null : s.key)}
              title={s.desc}
              className={`text-[11px] inline-flex items-center gap-1.5 border rounded-full px-2.5 py-1 transition-all ${s.soft} ${isActive ? 'ring-2 ring-offset-1 ring-violet-500 shadow-sm' : 'hover:shadow-sm'}`}
              data-testid={`scoring-chip-${s.key.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <span className={`inline-block w-2 h-2 rounded-full ${s.bar}`} />
              <span className="font-medium">{s.key}</span>
              <span className="text-slate-500">· {n}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────
// Committee Review Queue — assets that internal analysts promoted to
// "Recommended" for the client to decide on. Client clicks Approve /
// Shortlist / Reject. Writes back to committee_status on the
// engagement asset so internal CRM sees the decision live.
// ────────────────────────────────────────────────────────────────────
function ReviewQueueSection({ token, queue, onDecisionChange, statusFilter, setStatusFilter, cohortLocked, pipelineStage, setPipelineStage }) {
  const counts = queue?.counts || {};
  const active = queue?.active_review || queue?.assets || [];
  const reference = queue?.reference_library || [];

  // No client-side dropdowns — Priority TA + Phase 3+ are auto-applied
  // upstream in ViatrisClientDashboard via applyViatrisScope() so the
  // committee only sees their declared focus. Keeps the portal neat.
  //
  // Pipeline-stage filter (Feb 2026 · parity with Admin) — clicking a
  // stage tile above restricts the visible Review Queue accordingly.
  //   cohort / phase_3_plus  → show all in-scope active assets
  //   ta_of_interest         → drop off-strategy rows
  //   committee_review       → show active-review tiers only (already the default)
  //   finalised_assets       → only rows where all 6 reviewers submitted
  const visibleActive = active.filter((a) => {
    if (statusFilter) {
      const derived = a.derived_status || 'Pending';
      if (statusFilter === 'Scored') {
        if (!(derived === 'Approved' || derived === 'Rejected')) return false;
      } else if (derived !== statusFilter) {
        return false;
      }
    }
    if (pipelineStage === 'ta_of_interest' && a.off_strategy_reason) return false;
    if (pipelineStage === 'finalised_assets' || pipelineStage === 'finalised') {
      const derived = a.derived_status || 'Pending';
      if (!(derived === 'Approved' || derived === 'Rejected')) return false;
    }
    return true;
  });

  return (
    <section id="review-queue" className="scroll-mt-32" data-testid="review-queue">
      <Card className="border-violet-200 bg-gradient-to-br from-violet-50/40 to-indigo-50/30">
        <CardContent className="pt-5">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-violet-700 font-semibold mb-1 inline-flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" /> Committee Action Required
              </div>
              <h2 className="text-xl font-bold text-slate-800">Review Queue</h2>
              <p className="text-xs text-slate-600 mt-0.5">
                {queue?.cohort_name ? <>Cohort · <strong>{queue.cohort_name}</strong> · </> : null}
                {counts.active_total || 0} asset{(counts.active_total || 0) === 1 ? '' : 's'} to decide on.
                {reference.length > 0 && <> {reference.length} more available in the Reference Library (link below).</>}
              </p>
            </div>
            {statusFilter && (
              <div className="text-xs text-violet-700 inline-flex items-center gap-1">
                Filtering: <strong>{statusFilter}</strong>
                <button onClick={() => setStatusFilter(null)} className="text-violet-600 underline ml-1" data-testid="clear-kanban-filter">
                  clear filter
                </button>
              </div>
            )}
            {pipelineStage && setPipelineStage && (
              <div className="text-xs text-emerald-700 inline-flex items-center gap-1" data-testid="pipeline-stage-active">
                Pipeline stage: <strong>{
                  pipelineStage === 'ta_of_interest' ? 'TA of Interest'
                  : pipelineStage === 'committee_review' ? 'Committee Review'
                  : (pipelineStage === 'finalised_assets' || pipelineStage === 'finalised') ? 'Finalised'
                  : pipelineStage === 'phase_3_plus' ? 'Phase 3+'
                  : pipelineStage === 'eligible' ? 'Eligible for AI'
                  : pipelineStage === 'ai_scored' ? 'AI Assessed'
                  : 'Universe'
                }</strong>
                <button onClick={() => setPipelineStage(null)} className="text-emerald-700 underline ml-1" data-testid="clear-pipeline-filter">
                  clear
                </button>
              </div>
            )}
          </div>

          {/* Inline Scoring Progress — compact lifecycle tally pinned to
              the top of the Review Queue itself (instead of a separate
              section above). Three states: Pending → Under Review → Scored. */}
          <InlineScoringProgress
            counts={counts}
            total={active.length}
            activeFilter={statusFilter}
            onFilter={setStatusFilter}
          />

          {/* Reviewer role picker — picked ONCE for the entire Review
              Queue session. The chosen role then applies to every card
              below (each card embeds its own inline scoring matrix).
              Identity is cached in localStorage; cards re-read via the
              custom 'viatris-identity-changed' event. */}
          <ReviewerIdentityStrip />

          {visibleActive.length === 0 ? (
            <div className="text-center py-10 text-sm text-slate-500 bg-white/60 rounded border border-dashed border-slate-200">
              <BookOpen className="w-7 h-7 mx-auto mb-2 text-slate-300" />
              {active.length === 0 ? (
                <>
                  No assets are currently in your Review Queue.<br/>
                  <span className="text-[11px]">Assets the ForesightRx team marks as Recommended will appear here for your decision.</span>
                </>
              ) : (
                <>No assets match the <strong>{statusFilter}</strong> filter.</>
              )}
            </div>
          ) : (
            <HorizontalScrollStrip count={visibleActive.length}>
              {visibleActive.map((a) => (
                <ReviewCard key={a.ea_id} asset={a} token={token} onDecisionChange={onDecisionChange} cohortLocked={cohortLocked} />
              ))}
            </HorizontalScrollStrip>
          )}
        </CardContent>
      </Card>

      {/* Reference Library — complete asset index (all tiers, Strategic
          → Could Not Assess). Opens as a full standalone page in a new
          tab to avoid cluttering the committee's primary action surface. */}
      {(active.length + reference.length) > 0 && (() => {
        // Show committee both buckets: assets they're scoring (active)
        // and parked assets in their declared focus. Single CTA -> the
        // standalone library page handles every tier with filters.
        const totalIndexed = active.length + reference.length;
        return (
          <a
            href={`/client/viatris/${token}/reference`}
            className="block mt-4"
            data-testid="open-reference-library"
          >
            <Card className="border-slate-200 hover:border-indigo-300 hover:shadow-md transition-all cursor-pointer">
              <CardContent className="py-3 px-4 flex items-center gap-3">
                <BookOpen className="w-5 h-5 text-slate-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">
                    Reference Library
                  </div>
                  <div className="text-sm text-slate-700">
                    Complete index · <strong>{totalIndexed}</strong> asset{totalIndexed === 1 ? '' : 's'} evaluated across all tiers
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    Strategic Priority, High Priority, Medium, Monitor, Low Priority, Not Suitable — filter by tier on open.
                  </div>
                </div>
                <span className="inline-flex items-center gap-1 text-xs text-indigo-700 font-medium shrink-0">
                  Open <ChevronRight className="w-3.5 h-3.5" />
                </span>
              </CardContent>
            </Card>
          </a>
        );
      })()}
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────
// CohortOverviewCard — instructional paragraph + tier breakdown so the
// committee immediately understands what was analyzed, what was sent
// for their review, and what was filtered to the Reference Library.
// Designed to be self-explanatory ("you have 27 to decide on, here's
// the math, here's where the rest went").
// ────────────────────────────────────────────────────────────────────
function CohortOverviewCard({ counts, cohortName, universeTotal }) {
  const cohortTotal = counts.cohort_total || 0;
  const activeTotal = counts.active_total || 0;
  const refTotal = counts.reference_total || 0;
  const byTierActive = counts.by_tier_active || {};
  const byTierRef = counts.by_tier_reference || {};

  // Render nothing until the API has populated the cohort metadata.
  if (!cohortName && cohortTotal === 0) return null;

  // Compose the headline math: "16 High Priority + 11 Medium = 27".
  const activeOrder = ['Strategic Priority', 'High Priority', 'Medium'];
  const activeParts = activeOrder
    .map((t) => ({ tier: t, n: byTierActive[t] || 0 }))
    .filter((x) => x.n > 0);
  const refOrder = ['Monitor', 'Low Priority', 'Not Suitable', 'Could Not Assess', 'Awaiting AI'];
  const refParts = refOrder
    .map((t) => ({ tier: t, n: byTierRef[t] || 0 }))
    .filter((x) => x.n > 0);

  return (
    <section id="cohort-overview" className="scroll-mt-32 h-full" data-testid="cohort-overview">
      <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50/40 to-white h-full">
        <CardContent className="pt-4 pb-4 space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-indigo-700 font-semibold mb-1">
              How to use this page
            </div>
            <h2 className="text-lg font-bold text-slate-800">
              Engagement Overview · China Universe
            </h2>
          </div>

          <p className="text-sm text-slate-700 leading-relaxed">
            ForesightRx screened the full <strong>China biotech universe</strong>{universeTotal > 0 ? <> of <strong>{universeTotal}</strong> assets</> : null} against the engagement&rsquo;s screening criteria (see the Screening Rules panel above). <strong>{cohortTotal}</strong> asset{cohortTotal === 1 ? '' : 's'} passed the deterministic screen and {cohortTotal === 1 ? 'is' : 'are'} being evaluated across nine investment dimensions.
            After scoring, <strong>{activeTotal}</strong> asset{activeTotal === 1 ? '' : 's'}
            {activeParts.length > 0 ? (
              <>
                {' '}({activeParts.map((p, i) => (
                  <React.Fragment key={p.tier}>
                    {i > 0 && (i === activeParts.length - 1 ? ' + ' : ', ')}
                    <strong>{p.n}</strong> {p.tier}
                  </React.Fragment>
                ))})
              </>
            ) : null}
            {' '}{activeTotal === 1 ? 'was' : 'were'} judged worth your committee&rsquo;s attention this cycle and {activeTotal === 1 ? 'is' : 'are'} queued in the <strong>Committee Action Required</strong> section below.
            {refTotal > 0 && (
              <>
                {' '}The remaining <strong>{refTotal}</strong> asset{refTotal === 1 ? '' : 's'}
                {refParts.length > 0 ? (
                  <>
                    {' '}({refParts.map((p, i) => (
                      <React.Fragment key={p.tier}>
                        {i > 0 && (i === refParts.length - 1 ? ' + ' : ', ')}
                        <strong>{p.n}</strong> {p.tier}
                      </React.Fragment>
                    ))})
                  </>
                ) : null}
                {' '}{refTotal === 1 ? 'is' : 'are'} indexed in the <strong>Reference Library</strong> — every screened asset, across all tiers (including those still awaiting AI assessment), is available there for full transparency.
              </>
            )}
          </p>

          <div className="text-xs text-slate-600 leading-relaxed border-t border-indigo-100 pt-3">
            <strong className="text-slate-700">What to do next:</strong> open each asset in the Committee Action Required section, review the Executive Summary, and score the dimensions you can — 1–5 each. <em>Skip any dimension you can&rsquo;t confidently assess;</em> the committee weighted average is computed over dims you scored, so partial submissions are treated fairly. Once all 6 committee members have submitted at least one score, the consolidated verdict auto-derives —
            <Badge className="mx-1 text-[10px] bg-emerald-100 text-emerald-900 border border-emerald-300">In-License</Badge>{' '}(move to term sheet),
            <Badge className="mx-1 text-[10px] bg-sky-100 text-sky-900 border border-sky-300">Reassess</Badge>{' '}(hold for next milestone), or
            <Badge className="mx-1 text-[10px] bg-rose-100 text-rose-900 border border-rose-300">Decline</Badge>{' '}(pass).
          </div>
        </CardContent>
      </Card>
    </section>
  );
}


// ────────────────────────────────────────────────────────────────────
// HorizontalScrollStrip — wraps the Review Queue cards in a flex
// scroll container AND adds prev/next arrow buttons in the top-right
// so the committee can paginate through 27+ cards without dragging.
// ────────────────────────────────────────────────────────────────────
function HorizontalScrollStrip({ children, count }) {
  const scrollRef = React.useRef(null);
  const scroll = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    // Each "page" = the strip's visible width, which holds exactly 2
    // cards. So one arrow click = advance to the next set of 2.
    const delta = (direction === 'left' ? -1 : 1) * el.clientWidth;
    el.scrollBy({ left: delta, behavior: 'smooth' });
  };
  return (
    <>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] text-slate-400">
          {count} asset{count === 1 ? '' : 's'} · 1 per view · use the arrows →
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => scroll('left')}
            className="border border-slate-200 rounded-full p-1 hover:bg-slate-100 transition-colors"
            data-testid="scroll-left-btn"
            aria-label="Scroll left"
          >
            <ChevronLeft className="w-4 h-4 text-slate-600" />
          </button>
          <button
            onClick={() => scroll('right')}
            className="border border-slate-200 rounded-full p-1 hover:bg-slate-100 transition-colors"
            data-testid="scroll-right-btn"
            aria-label="Scroll right"
          >
            <ChevronRight className="w-4 h-4 text-slate-600" />
          </button>
        </div>
      </div>
      <div
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto pb-3 -mx-1 px-1 snap-x snap-mandatory scroll-smooth"
        data-testid="review-queue-strip"
      >
        {children}
      </div>
    </>
  );
}


function ReviewCard({ asset, token, onDecisionChange, cohortLocked }) {
  const [showDeepDive, setShowDeepDive] = useState(false);
  const [rubricOpen, setRubricOpen] = useState(false);

  // Sep 2026: decisions are no longer manual — they auto-derive from the
  // 6-reviewer scoring. The card embeds a compact inline scoring matrix
  // so reviewers can score without leaving the queue. Identity is picked
  // ONCE at the queue level (localStorage.viatris.member_id).
  const myMemberId = (() => {
    try { return localStorage.getItem('viatris.member_id') || null; }
    catch { return null; }
  })();
  const [scoreData, setScoreData] = useState(null);   // GET /member-scores response
  const [draft, setDraft] = useState({});
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [identityVersion, setIdentityVersion] = useState(0); // bump to re-read localStorage
  const [confirmLock, setConfirmLock] = useState(false);     // show role-lock confirm modal

  // Re-read identity from localStorage when the queue header changes it.
  React.useEffect(() => {
    const handler = () => setIdentityVersion((v) => v + 1);
    window.addEventListener('viatris-identity-changed', handler);
    return () => window.removeEventListener('viatris-identity-changed', handler);
  }, []);

  // Refs mirror draft/comment so `loadScores` can compare server-vs-
  // local state without adding them to its deps (which would recreate
  // loadScores on every keystroke and re-fire its parent effect).
  const draftRef = React.useRef(draft);
  const commentRef = React.useRef(comment);
  React.useEffect(() => { draftRef.current = draft; }, [draft]);
  React.useEffect(() => { commentRef.current = comment; }, [comment]);

  const loadScores = React.useCallback(async () => {
    try {
      const q = myMemberId ? `?as=${encodeURIComponent(myMemberId)}` : '';
      const r = await axios.get(
        `${API}/client/viatris/${token}/assets/${asset.ea_id}/member-scores${q}`,
      );
      setScoreData(r.data);
      // Pre-populate draft from any prior submission by me.
      // (Feb 2026 hot-fix — refresh loop) Only reset local draft when
      // the server-side scores DIFFER from what the reviewer is
      // actively editing. Otherwise every server round-trip stomped
      // the in-flight edit, which re-triggered auto-save → loop.
      const me = (r.data.members || []).find((m) => m.member_id === myMemberId);
      const curDraft   = draftRef.current   || {};
      const curComment = commentRef.current || '';
      if (me) {
        const serverKey = JSON.stringify({ s: me.scores || {}, c: me.comment || '' });
        const localKey  = JSON.stringify({ s: curDraft,        c: curComment });
        if (serverKey !== localKey) {
          setDraft(me.scores || {});
          setComment(me.comment || '');
        }
      } else if (!Object.keys(curDraft).length && !curComment) {
        // Only clear when we haven't started editing yet — never wipe
        // an in-progress edit because of a server round-trip.
        setDraft({});
        setComment('');
      }
    } catch {/* ignore */}
  }, [token, asset.ea_id, myMemberId]);
  React.useEffect(() => { loadScores(); }, [loadScores, identityVersion]);

  // (Feb 2026 policy) A reviewer has "submitted" as soon as they have
  // at least one numeric score on the asset. Blinding + resubmit UI
  // key off this — full-9 scoring is no longer required.
  const mySubmitted = !!myMemberId && (scoreData?.members || []).some(
    (m) => m.member_id === myMemberId
       && SCORING_DIMS.some((d) => _hasAnswer(m.scores?.[d])),
  );
  const anyDimsInDraft = SCORING_DIMS.some((d) => _hasAnswer(draft[d]));

  const submitScores = async () => {
    if (!myMemberId || !anyDimsInDraft) return;
    // First submission under this role triggers the lock-confirmation
    // modal. After confirmation, the role becomes permanently bound to
    // this browser session — no Change role button.
    const isLocked = (() => {
      try { return localStorage.getItem('viatris.member_id_locked') === '1'; }
      catch { return false; }
    })();
    if (!isLocked) {
      setConfirmLock(true);
      return;
    }
    await persistScores();
  };

  const persistScores = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/client/viatris/${token}/assets/${asset.ea_id}/member-scores`,
        { member_id: myMemberId, scores: draft, comment },
      );
      await loadScores();
      if (onDecisionChange) await onDecisionChange();
    } catch (e) {
      alert('Save failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const confirmAndPersist = async () => {
    try { localStorage.setItem('viatris.member_id_locked', '1'); } catch { /* noop */ }
    window.dispatchEvent(new Event('viatris-identity-changed'));
    setConfirmLock(false);
    await persistScores();
  };

  // ────────────────────────────────────────────────────────────────
  // Auto-save on dropdown change (Feb 2026) — the moment a reviewer
  // picks ANY dropdown value, we silently persist the draft so the
  // asset immediately flips from "Pending" → "Under Review" on every
  // committee surface (kanban tab, cohort table, review queue chip).
  // Debounced 900 ms so bursts of clicks coalesce into one POST.
  // Silently locks the role on the very first auto-save (no confirm
  // modal — the reviewer has already picked their identity and is
  // actively scoring, so the modal would be redundant noise).
  //
  // (Feb 2026 hot-fix — refresh loop) Two rules must be strictly
  // enforced here or the parent re-renders in a tight loop:
  //   1. We DO NOT reload local scores after saving. The server is
  //      authoritative; but the local draft is what the reviewer is
  //      actively editing. Re-hydrating draft from the server round-
  //      trip caused the draft-init effect to reset draft, which
  //      re-triggered the save effect, which re-fetched, ad infinitum.
  //   2. We call `onDecisionChange` (which is the parent-level "reload
  //      the overview") ONCE per asset — the first time this asset
  //      transitions from Pending → Under Review. Subsequent dropdown
  //      changes stay purely local; the parent already knows the
  //      asset is Under Review.
  // ────────────────────────────────────────────────────────────────
  const [autoSaveState, setAutoSaveState] = useState('idle');   // 'idle' | 'saving' | 'saved'
  const lastAutoSavedRef = React.useRef('');
  const parentNotifiedRef = React.useRef(false);
  React.useEffect(() => {
    if (!myMemberId) return;
    if (!anyDimsInDraft) return;
    // Serialise draft + comment so we only re-save when something changed.
    const key = JSON.stringify({ d: draft, c: comment });
    if (key === lastAutoSavedRef.current) return;
    const handle = setTimeout(async () => {
      setAutoSaveState('saving');
      try {
        // Silent lock — no confirmation modal on auto-save.
        // (Feb 2026 hot-fix — refresh loop) The identity-changed event
        // was previously dispatched here to unlock the "already picked
        // your role" chip elsewhere. But parents that listen to that
        // event re-fetch committee state, which cascades into a full
        // reset of local `draft` → re-fires this effect → infinite
        // loop. The localStorage flag alone is enough — anyone reading
        // it on next mount will see the lock without needing an event.
        try { localStorage.setItem('viatris.member_id_locked', '1'); } catch { /* noop */ }
        await axios.post(
          `${API}/client/viatris/${token}/assets/${asset.ea_id}/member-scores`,
          { member_id: myMemberId, scores: draft, comment },
        );
        lastAutoSavedRef.current = key;
        setAutoSaveState('saved');
        // (Feb 2026 EMERGENCY hot-fix — production 2-second refresh loop)
        // Previously we called `onDecisionChange` on the first auto-save
        // per asset to bump the parent's kanban counters. But calling
        // the parent's loadAll re-renders the ReviewCard with a fresh
        // asset prop reference, which resets `parentNotifiedRef`, which
        // re-fires `onDecisionChange` on the next auto-save, which
        // re-renders... a hard infinite loop. The parent will pick up
        // the "Under Review" status naturally the next time the user
        // navigates or manually refreshes — that's an acceptable delay.
        // Never fire onDecisionChange from auto-save.
        // Fade the "Saved" pill after a couple seconds.
        setTimeout(() => setAutoSaveState((s) => s === 'saved' ? 'idle' : s), 2400);
      } catch {
        // Silent failure — the explicit Submit button remains as a
        // fallback so the reviewer always has a recovery path.
        setAutoSaveState('idle');
      }
    }, 900);
    return () => clearTimeout(handle);
    // NOTE: `anyDimsInDraft` intentionally OMITTED from deps — it's a
    // derived boolean that flips only from false→true on the first
    // score. Including it caused a spurious re-render loop when the
    // effect re-scheduled a timeout as the ref bookkeeping flipped.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, comment, myMemberId]);

  // Delete the requesting reviewer's submission for this asset. Wired
  // to a confirmation modal in InlineScoringSubmitted; every call
  // writes an audit row server-side (Option B audit-log posture).
  const deleteMyScores = async () => {
    if (!myMemberId) return;
    setSaving(true);
    try {
      await axios.delete(
        `${API}/client/viatris/${token}/assets/${asset.ea_id}/member-scores`,
        { params: { member_id: myMemberId } },
      );
      setDraft({});
      setComment('');
      await loadScores();
      if (onDecisionChange) await onDecisionChange();
    } catch (e) {
      alert('Delete failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };
  // Tint reflects DERIVED status (not manual decision anymore).
  const derived = asset.derived_status || 'Pending';
  const cardTint =
    derived === 'Approved'      ? 'border-emerald-300 bg-emerald-50/40'
  : derived === 'Rejected'      ? 'border-slate-300 bg-slate-50/40 opacity-80'
  : derived === 'Under Review'  ? 'border-amber-300 bg-amber-50/40'
                                : 'border-slate-200 bg-white';

  return (
    <>
      <Card
        className={`${cardTint} ${asset.human_review_pending ? 'ring-1 ring-amber-200' : ''} transition-all snap-start shrink-0 basis-full min-w-full flex flex-col`}
        data-testid={`review-card-${asset.ea_id}`}
      >
        <CardContent className="py-4 px-4 flex-1">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-5">
            {/* ─────────────────── LEFT: Asset content ─────────────────── */}
            <div className="flex flex-col min-w-0">
              {/* Top stripe — phase · composite · tier · derived status */}
              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <StagePill phase={asset.phase} />
                <div className="flex items-center gap-1.5 flex-wrap">
                  {asset.human_review_pending && (
                    <Badge
                      className="text-[9px] border bg-amber-50 text-amber-800 border-amber-300"
                      title="This asset's AI assessment is still being reviewed by a ForesightRx analyst. The content shown is an AI draft and may change before it is finalised."
                      data-testid={`review-card-pending-${asset.ea_id}`}
                    >
                      AI draft · pending human review
                    </Badge>
                  )}
                  {asset.composite_score != null && (
                    <span
                      className="inline-flex items-baseline gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 border border-indigo-200 text-indigo-800"
                      title="Weighted average per scored dimension (1–5 scale)"
                      data-testid={`review-card-score-${asset.ea_id}`}
                    >
                      <span className="font-bold text-xs">{(asset.weighted_avg_score ?? asset.composite_score / 9).toFixed(2)}</span>
                      <span className="text-[9px] opacity-70">/5</span>
                    </span>
                  )}
                  {asset.recommendation && (
                    <span className="inline-flex items-center gap-1">
                      <Badge
                        className={`text-[9px] border ${TIER_BADGE[asset.recommendation] || 'bg-slate-100 text-slate-700'}`}
                        title={TIER_EXPLAINER[asset.recommendation] || asset.recommendation}
                        data-testid={`review-card-tier-${asset.ea_id}`}
                      >
                        {asset.recommendation}
                      </Badge>
                      <DefinitionInfo defKey={tierDefKey(asset.recommendation)} />
                      <WhyRecommendationInfo asset={asset} testIdPrefix={`review-card-rec`} />
                    </span>
                  )}
                  {/* Feb 2026 · user request "you can remove all these
                      tags" — AIConcernChip and OffStrategyChip are now
                      no-ops. The rationale for the tier (including
                      elevation/downgrade cases and off-strategy rows)
                      is surfaced through the single (i) icon above,
                      matching the Admin Workbench for consistency. */}
                  <Badge className={`text-[10px] ${DECISION_BADGE[derived] || ''}`}>
                    {derived}
                  </Badge>
                </div>
              </div>

              {/* Asset name + chips */}
              <div className="font-bold text-lg text-slate-800 mb-1 leading-tight" title={asset.asset_name}>
                {asset.asset_name}
              </div>
              <div className="flex items-center gap-1 flex-wrap text-[10px] mb-3">
                {asset.indication && <Badge variant="outline" className="text-[10px]">{asset.indication}</Badge>}
                {asset.modality && <Badge variant="outline" className="text-[10px]">{asset.modality}</Badge>}
                {asset.phase && <Badge variant="outline" className="text-[10px] border-violet-300 text-violet-800">Stage: {asset.phase}</Badge>}
              </div>

              {/* Headline + strengths/risks — now with more space we can show
                  up to 4 bullets each and longer lines. */}
              <div className="space-y-3">
                {asset.headline && (
                  <p className="text-sm text-slate-700 italic" title={asset.headline}>
                    &ldquo;{asset.headline}&rdquo;
                  </p>
                )}
                {(asset.key_strengths || []).length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold mb-1">Strengths</div>
                    <ul className="list-disc pl-5 text-xs text-slate-700 space-y-1">
                      {asset.key_strengths.slice(0, 4).map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  </div>
                )}
                {(asset.key_risks || []).length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-rose-700 font-semibold mb-1">Risks</div>
                    <ul className="list-disc pl-5 text-xs text-slate-700 space-y-1">
                      {asset.key_risks.slice(0, 4).map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  </div>
                )}
              </div>

              {/* Deep Dive link */}
              <button
                type="button"
                onClick={() => setShowDeepDive(true)}
                className="text-xs text-indigo-700 hover:underline mt-3 self-start inline-flex items-center gap-1"
                data-testid={`review-card-deep-dive-${asset.ea_id}`}
              >
                Deep Dive <ChevronRight className="w-3 h-3" />
              </button>

              {/* (Feb 2026 — user request 1 Jul 2026) Math anchor legend
                  + scoring-rubric link BELOW the Deep Dive button, INSIDE
                  the review card, so reviewers see the tier framing
                  inline while evaluating an asset — no need to scroll
                  down to the Cohort Scoring Matrix. Removed from that
                  matrix header in the same commit. */}
              <div className="mt-3 pt-3 border-t border-slate-200/60 space-y-1">
                <div className="flex items-center gap-1.5 flex-wrap text-[10px]">
                  <span className="text-slate-500 font-medium mr-0.5">Math anchors ·</span>
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-emerald-100 border border-emerald-300 text-emerald-800 font-medium">Strategic ≥ 4.5</span>
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-teal-100 border border-teal-300 text-teal-800 font-medium">High 3.75–4.5</span>
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-100 border border-amber-300 text-amber-800 font-medium">Medium 3.0–3.75</span>
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-orange-100 border border-orange-300 text-orange-800 font-medium">Monitor 2.0–3.0</span>
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-rose-100 border border-rose-300 text-rose-800 font-medium">Low &lt; 2.0</span>
                  <span className="text-slate-400">on Weighted Avg / 5</span>
                </div>
                <div className="text-[10px] text-slate-500 italic">
                  Displayed tier is an AI-holistic call (IP, competition, commercial fit, modality). Where the row is off its math band, the <Info className="inline w-2.5 h-2.5 mx-0.5 -mt-0.5" /> chip next to the tier badge above shows the specific rationale.{' '}
                  <button
                    type="button"
                    onClick={() => setRubricOpen(true)}
                    className="not-italic font-semibold text-indigo-700 hover:text-indigo-900 underline decoration-dotted underline-offset-2 ml-0.5"
                    data-testid={`open-scoring-rubric-card-${asset.ea_id}`}
                  >
                    See scoring rubric →
                  </button>
                </div>
              </div>
            </div>

            {/* ─────────────────── RIGHT: Scoring panel (full height) ─────────────────── */}
            <div className="lg:border-l lg:border-slate-200/60 lg:pl-5 border-t border-slate-200/60 pt-3 lg:border-t-0 lg:pt-0">
              {!myMemberId ? (
                <div className="text-xs text-slate-500 italic">
                  Pick your reviewer role at the top of the Review Queue to start scoring.
                </div>
              ) : mySubmitted ? (
                <InlineScoringSubmitted
                  scoreData={scoreData}
                  myMemberId={myMemberId}
                  draft={draft}
                  setDraft={setDraft}
                  comment={comment}
                  setComment={setComment}
                  onResubmit={cohortLocked ? null : submitScores}
                  onDelete={cohortLocked ? null : deleteMyScores}
                  saving={saving}
                  anyDimsInDraft={anyDimsInDraft}
                  eaId={asset.ea_id}
                  cohortLocked={cohortLocked}
                />
              ) : cohortLocked ? (
                <div className="text-xs text-slate-500 italic bg-slate-50 border border-slate-200 rounded px-2 py-2">
                  🔒 Cohort locked · scoring is read-only for this asset.
                </div>
              ) : (
                <InlineScoringForm
                  aiScoring={null}
                  draft={draft}
                  setDraft={setDraft}
                  comment={comment}
                  setComment={setComment}
                  onSubmit={submitScores}
                  saving={saving}
                  anyDimsInDraft={anyDimsInDraft}
                  myMemberId={myMemberId}
                  scoreData={scoreData}
                  eaId={asset.ea_id}
                  autoSaveState={autoSaveState}
                />
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {showDeepDive && (
        <DeepDiveModal
          token={token}
          asset={asset}
          onClose={() => setShowDeepDive(false)}
        />
      )}

      {/* Per-card Scoring Rubric modal — triggered from the math-anchor
          strip below the Deep Dive button. Local state so multiple cards
          don't collide over a shared modal instance. */}
      {rubricOpen && <ScoringRubricModal onClose={() => setRubricOpen(false)} />}

      {/* First-submission confirmation — locks the reviewer role
          permanently to this browser session once the reviewer accepts. */}
      {confirmLock && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          data-testid="role-lock-confirm-modal"
        >
          <div className="bg-white rounded-lg max-w-md w-full p-5 shadow-2xl">
            <div className="flex items-center gap-2 mb-3">
              <Lock className="w-5 h-5 text-amber-600" />
              <h3 className="text-base font-bold text-slate-900">Lock your reviewer role?</h3>
            </div>
            <p className="text-sm text-slate-700 leading-relaxed mb-2">
              You are about to submit your first scores as
              <strong className="text-indigo-700"> {ROLE_LABEL[myMemberId]}</strong>.
            </p>
            <p className="text-sm text-slate-700 leading-relaxed mb-4">
              Once confirmed, <strong>your role will be permanently locked for this committee cycle</strong>.
              You will not be able to change roles or submit scores under a different role. You can still
              revise your own scores any time.
            </p>
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConfirmLock(false)}
                className="text-xs h-8"
                data-testid="role-lock-cancel"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={confirmAndPersist}
                disabled={saving}
                className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs h-8"
                data-testid="role-lock-confirm"
              >
                {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Lock className="w-3 h-3 mr-1" />}
                Confirm & Submit
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


// Compact inline 9-criteria scoring grid — embedded directly on each
// Review Queue card. Pre-submit state (reviewer entering scores).
// (Feb 2026 policy) Partial submissions are accepted — a reviewer may
// only feel expert in a subset of the 9 dims. Submit is enabled as
// soon as at least one dim is scored.
function InlineScoringForm({ draft, setDraft, comment, setComment, onSubmit, saving, anyDimsInDraft, myMemberId, scoreData, eaId, autoSaveState }) {
  const nScored = (scoreData?.members || []).length;
  const nDraft = Object.values(draft).filter(_hasAnswer).length;
  return (
    <div className="space-y-2" data-testid={`inline-scoring-form-${eaId}`}>
      <div className="text-[10px] uppercase tracking-wide text-indigo-700 font-semibold flex items-center justify-between">
        <span>Your scoring · score the dims you can · 1–5</span>
        <span className="text-slate-500 text-[9px] font-normal flex items-center gap-1.5">
          {/* Auto-save chip — surfaces the moment the reviewer's very
              first dropdown selection persists to the server (which
              is what flips the asset to "Under Review" upstream). */}
          {autoSaveState === 'saving' && (
            <span className="inline-flex items-center gap-1 text-amber-700" data-testid={`autosave-saving-${eaId}`}>
              <Loader2 className="w-2.5 h-2.5 animate-spin" />
              Saving…
            </span>
          )}
          {autoSaveState === 'saved' && (
            <span className="inline-flex items-center gap-1 text-emerald-700" data-testid={`autosave-saved-${eaId}`}>
              <Check className="w-2.5 h-2.5" />
              Saved · Under Review
            </span>
          )}
          <span>{nScored} of 6 reviewers in</span>
        </span>
      </div>
      <div className="space-y-1.5">
        {SCORING_DIMS.map((dim, idx) => (
          <div key={dim} className="flex items-center justify-between gap-3 bg-slate-50 rounded px-2.5 py-1.5 border border-slate-200">
            <span className="text-xs text-slate-700 leading-tight inline-flex items-center gap-1.5" title={DIM_LABELS[dim]}>
              <span className="text-[10px] font-mono text-slate-400 tabular-nums w-4 text-right">{idx + 1}.</span>
              {DIM_LABELS[dim]}
            </span>
            <select
              value={draft[dim] ?? ''}
              onChange={(e) => setDraft({ ...draft, [dim]: _parseScoreInput(e.target.value) })}
              className="text-xs border border-indigo-300 rounded px-1.5 py-0.5 bg-white w-20 shrink-0"
              data-testid={`inline-score-${eaId}-${dim}`}
            >
              <option value="">—</option>
              <option value="skip">Skip</option>
              {[1,2,3,4,5].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-slate-500 leading-snug">
        Use <strong>Skip</strong> for any dim you can&rsquo;t assess — skips are excluded from the committee weighted average, so partial submissions are treated fairly.
      </p>
      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="Optional rationale for your scoring…"
        rows={1}
        className="w-full text-[11px] border border-slate-200 rounded p-1.5 resize-none focus:border-indigo-400 focus:outline-none"
        data-testid={`inline-comment-${eaId}`}
      />
      <Button
        size="sm"
        onClick={onSubmit}
        disabled={saving || !anyDimsInDraft}
        className="bg-indigo-600 hover:bg-indigo-700 text-white h-7 text-[11px] w-full disabled:opacity-50"
        data-testid={`inline-submit-${eaId}`}
      >
        {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
        Submit my scores ({nDraft}/{SCORING_DIMS.length} dims)
      </Button>
    </div>
  );
}

// Post-submit state — committee progress + your scores + consensus
// stats (revealed because the requesting reviewer has fully scored).
function InlineScoringSubmitted({ scoreData, myMemberId, draft, setDraft, comment, setComment, onResubmit, onDelete, saving, anyDimsInDraft, eaId, cohortLocked }) {
  const me = (scoreData?.members || []).find((m) => m.member_id === myMemberId);
  const myScoredVals = SCORING_DIMS
    .map((d) => me?.scores?.[d])
    .filter((v) => typeof v === 'number');
  const mySkipCount = SCORING_DIMS.filter((d) => _isSkip(me?.scores?.[d])).length;
  const myAnsweredCount = myScoredVals.length + mySkipCount;
  const myWeightedAvg = myScoredVals.length
    ? (myScoredVals.reduce((a, b) => a + b, 0) / myScoredVals.length)
    : null;
  const nSubmitted = scoreData?.members_submitted ?? 0;
  const nFull = scoreData?.members_full_scored ?? 0;
  const overallAvg = scoreData?.committee_overall_avg;
  const stddev = scoreData?.consensus_overall?.stddev;
  const derived = scoreData?.derived_status || 'Pending';
  const [editing, setEditing] = useState(false);
  // Confirmation prompts (Option B integrity posture) — every Revise
  // and Delete is gated by an explicit dialog warning the reviewer
  // the action will be logged for the FRx analyst team.
  const [showReviseConfirm, setShowReviseConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  return (
    <div className="space-y-2" data-testid={`inline-scoring-submitted-${eaId}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] text-emerald-700 font-semibold inline-flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> Your scores submitted · weighted avg{' '}
          {myWeightedAvg != null ? myWeightedAvg.toFixed(2) : '—'}/5
          <span className="text-slate-400 font-normal text-[10px]">
            ({myScoredVals.length}/{SCORING_DIMS.length} dims{mySkipCount > 0 ? ` · ${mySkipCount} skipped` : ''})
          </span>
        </div>
        <Badge className={`text-[10px] ${DECISION_BADGE[derived] || ''}`}>{derived}</Badge>
      </div>
      <div className="grid grid-cols-2 gap-2 text-[10px] bg-slate-50 rounded p-2 border border-slate-200">
        <div><strong className="text-slate-700">Committee progress:</strong> {nSubmitted} of 6 submitted{nFull < nSubmitted ? ` · ${nFull} scored every dim` : ''}</div>
        <div><strong className="text-slate-700">Committee weighted avg:</strong> {overallAvg != null ? `${overallAvg}/5` : '—'}</div>
        <div><strong className="text-slate-700">Disagreement (σ):</strong> {stddev != null ? stddev.toFixed(2) : '—'}</div>
        <div><strong className="text-slate-700">Reviewers in:</strong> {(scoreData?.members || []).length}</div>
      </div>
      {editing ? (
        <InlineScoringForm
          draft={draft} setDraft={setDraft}
          comment={comment} setComment={setComment}
          onSubmit={async () => { await onResubmit(); setEditing(false); }}
          saving={saving} anyDimsInDraft={anyDimsInDraft}
          myMemberId={myMemberId} scoreData={scoreData} eaId={eaId}
        />
      ) : (
        <div className="flex items-center justify-between gap-3">
          {cohortLocked ? (
            <span className="text-[10px] text-slate-500 italic inline-flex items-center gap-1">
              🔒 Cohort locked — submission read-only
            </span>
          ) : (
            <button
              type="button"
              onClick={() => setShowReviseConfirm(true)}
              className="text-[10px] text-indigo-600 hover:underline"
              data-testid={`inline-edit-${eaId}`}
            >
              Revise my scores ›
            </button>
          )}
          {!cohortLocked && onDelete && (
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={saving}
              className="text-[10px] text-rose-600 hover:underline disabled:opacity-50"
              data-testid={`inline-delete-${eaId}`}
            >
              Delete my scores
            </button>
          )}
        </div>
      )}

      {/* Revise confirmation modal */}
      <ConfirmScoreActionModal
        open={showReviseConfirm}
        title="Revise your submitted scores?"
        accent="indigo"
        actionLabel="Yes, revise my scores"
        testid={`revise-confirm-${eaId}`}
        body={
          <>
            <p>
              You are about to <strong>edit a submission you already locked in</strong>.
              Your previous scores will be replaced once you re-submit.
            </p>
            <p>
              This action is <strong>recorded in the audit log</strong> and shared with the
              ForesightRx analyst team along with both the previous and new values, so the
              committee record stays defensible.
            </p>
          </>
        }
        onCancel={() => setShowReviseConfirm(false)}
        onConfirm={() => {
          setShowReviseConfirm(false);
          setEditing(true);
        }}
      />

      {/* Delete confirmation modal */}
      <ConfirmScoreActionModal
        open={showDeleteConfirm}
        title="Delete your submitted scores?"
        accent="rose"
        actionLabel="Yes, delete my scores"
        testid={`delete-confirm-${eaId}`}
        body={
          <>
            <p>
              Your entire submission for this asset will be removed and the card will return
              to its pre-submit state. You can re-score the asset afterwards.
            </p>
            <p>
              This action is <strong>recorded in the audit log</strong> with a snapshot of the
              scores being removed, so the analyst team can see exactly what was wiped.
            </p>
          </>
        }
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={async () => {
          setShowDeleteConfirm(false);
          await onDelete();
        }}
      />
    </div>
  );
}


// Generic confirmation modal for the Revise / Delete actions on the
// client portal. Keeps copy + audit-log warning consistent across both
// flows (Option B integrity posture, September 2026).
function ConfirmScoreActionModal({ open, title, body, accent, actionLabel, onCancel, onConfirm, testid }) {
  if (!open) return null;
  const accentBtn = accent === 'rose'
    ? 'bg-rose-600 hover:bg-rose-700'
    : 'bg-indigo-600 hover:bg-indigo-700';
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      data-testid={testid}
    >
      <div className="bg-white rounded-lg max-w-md w-full p-5 shadow-2xl">
        <h3 className="text-base font-bold text-slate-900 mb-2">{title}</h3>
        <div className="text-sm text-slate-700 leading-relaxed space-y-2 mb-4">{body}</div>
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onCancel}
            className="text-xs h-8"
            data-testid={`${testid}-cancel`}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={onConfirm}
            className={`${accentBtn} text-white text-xs h-8`}
            data-testid={`${testid}-confirm`}
          >
            {actionLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}


function formatBytes(n) {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}


// Reviewer identity strip — sits at the top of the Review Queue.
// Picks the reviewer's role ONCE for the whole session. The "Change
// role" link is removed permanently after the reviewer submits their
// first score under that role (lock flag in localStorage), so a
// reviewer cannot swap roles mid-cohort.
function ReviewerIdentityStrip() {
  const [myMemberId, setMyMemberId] = useState(() => {
    try { return localStorage.getItem('viatris.member_id') || null; }
    catch { return null; }
  });
  const [locked, setLocked] = useState(() => {
    try { return localStorage.getItem('viatris.member_id_locked') === '1'; }
    catch { return false; }
  });

  // Listen for the lock event from the inline scoring submit handler.
  React.useEffect(() => {
    const onChange = () => {
      try {
        setMyMemberId(localStorage.getItem('viatris.member_id') || null);
        setLocked(localStorage.getItem('viatris.member_id_locked') === '1');
      } catch { /* noop */ }
    };
    window.addEventListener('viatris-identity-changed', onChange);
    return () => window.removeEventListener('viatris-identity-changed', onChange);
  }, []);

  const pick = (id) => {
    try { localStorage.setItem('viatris.member_id', id); } catch { /* noop */ }
    setMyMemberId(id);
    window.dispatchEvent(new Event('viatris-identity-changed'));
  };
  const clear = () => {
    if (locked) return; // hard-stop: locked roles cannot be changed
    try {
      localStorage.removeItem('viatris.member_id');
      localStorage.removeItem('viatris.member_id_locked');
    } catch { /* noop */ }
    setMyMemberId(null);
    window.dispatchEvent(new Event('viatris-identity-changed'));
  };

  const myLabel = ROLE_LABEL[myMemberId] || myMemberId;
  return (
    <div className="bg-indigo-50/60 border border-indigo-200 rounded p-2.5 mb-3" data-testid="reviewer-identity-strip">
      {myMemberId ? (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-xs text-slate-700">
            <Sparkles className="w-3 h-3 inline mr-1 text-indigo-600" />
            You are scoring as <strong className="text-indigo-700">{myLabel}</strong>.
            {locked ? (
              <span className="ml-1 inline-flex items-center gap-1 text-[10px] text-emerald-700 font-semibold">
                <Lock className="w-3 h-3" /> Role locked
              </span>
            ) : (
              <span className="text-slate-500 ml-1">Scores are blinded until you submit per asset.</span>
            )}
          </div>
          {/* Sep 2026 — role lock is intentionally editable while we
              are still in the test phase. Production posture will
              flip this to "FRx-team unlock only". */}
          <button
            onClick={() => {
              if (locked && !window.confirm(
                'Change reviewer role? Your previous role will be cleared so you can score as someone else. This is recorded in the audit log.',
              )) return;
              clear();
            }}
            className="text-[11px] text-indigo-600 hover:underline"
            data-testid="reviewer-identity-change"
          >
            Change role
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="text-xs text-slate-700">
            <strong className="text-indigo-800">Pick your reviewer role</strong> to start scoring assets below.
            You will see only the AI suggested score until you submit your own scores on each asset.
            <span className="block text-[10px] text-amber-700 mt-0.5">
              Note: once you submit your first scores, the role will be locked for this committee cycle. (You can still change it via the &quot;Change role&quot; link — every change is audited.)
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {REVIEWER_ROLES.map((r) => (
              <button
                key={r.id}
                onClick={() => pick(r.id)}
                className="text-[11px] px-2.5 py-1 rounded border border-indigo-300 bg-white hover:bg-indigo-100 text-indigo-800 transition-colors"
                data-testid={`reviewer-pick-${r.id}`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// (Feb 2026) FinalExecutiveSummarySection removed — the asset-level
// Hero Summary popup (opens on Verdict-cell click in the Cohort
// Scoring Matrix) now consolidates AI exec summary + per-dimension
// scoring + AI concerns + ForesightRx Opportunity per asset. That
// surface is what will drive report generation, so the standalone
// "Overall Recommendations" section is no longer needed.





function SectionHeader({ title, subtitle, badge }) {
  return (
    <div className="mb-3 flex items-end justify-between gap-3 flex-wrap">
      <div>
        <h2 className="text-lg font-bold text-slate-800">{title}</h2>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      {badge && <Badge variant="outline" className="text-[10px]">{badge}</Badge>}
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────
// CohortStrip — top-of-page reporting cycles. Acts both as cohort
// history and as the cohort selector. "Overall" tile (no selection)
// shows every cohort merged; clicking any cohort scopes the rest of
// the dashboard to it.
// ────────────────────────────────────────────────────────────────────
function CohortStrip({ cohorts, activeCohortName, selectedCohort, setSelectedCohort, pipelineCounts = {} }) {
  const stats = [
    { key: 'universe',         label: 'China Universe screened', value: pipelineCounts.universe },
    { key: 'eligible',         label: 'Eligible for AI',         value: pipelineCounts.eligible },
    { key: 'committee_review', label: 'In Committee Review',     value: pipelineCounts.committee_review },
  ];
  return (
    <section id="reporting-cycles" className="scroll-mt-32 h-full" data-testid="reporting-cycles">
      <Card className="h-full">
        <CardContent className="pt-4 pb-4 space-y-3">
          <div>
            <h2 className="text-base font-bold text-slate-800 leading-tight">Reporting Cycle</h2>
            <p className="text-[10px] text-slate-500 leading-tight mt-0.5">
              Current review cycle · numbers drawn live from the China universe
            </p>
          </div>

          {/* Live universe-derived stats for the current cycle. */}
          <div className="space-y-1" data-testid="cycle-stats">
            {stats.map((s) => (
              <div
                key={s.key}
                className="flex items-baseline justify-between gap-2 border border-slate-200 bg-slate-50/60 rounded-md px-2.5 py-1.5"
                data-testid={`cycle-stat-${s.key}`}
              >
                <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">{s.label}</div>
                <div className="text-sm font-bold font-mono text-slate-800 tabular-nums">
                  {(s.value ?? 0).toLocaleString()}
                </div>
              </div>
            ))}
          </div>

          {/* Cycle history — name + status + date. No stale asset counts. */}
          <div className="space-y-1.5" data-testid="cohort-strip">
            {cohorts.map((c) => {
              const isSelected = selectedCohort
                ? selectedCohort === c.name
                : c.name === activeCohortName;
              return (
                <button
                  key={c.name}
                  onClick={() => setSelectedCohort(c.name)}
                  className={`w-full text-left border rounded-md px-2.5 py-1.5 transition-all ${
                    isSelected
                      ? 'border-violet-400 bg-violet-50 ring-1 ring-violet-400'
                      : 'border-slate-200 bg-white hover:border-violet-300'
                  }`}
                  data-testid={`cohort-tile-${c.name.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs font-bold text-slate-800 truncate" title={c.name}>{c.name}</div>
                    {c.status === 'completed' ? (
                      <Badge className="text-[9px] bg-slate-200 text-slate-700 shrink-0 px-1.5 py-0">Completed{c.completed_at ? ` ${c.completed_at.slice(0, 10)}` : ''}</Badge>
                    ) : c.locked ? (
                      <Badge className="text-[9px] bg-emerald-100 text-emerald-800 shrink-0 px-1.5 py-0">Locked</Badge>
                    ) : (
                      <Badge className="text-[9px] bg-amber-100 text-amber-800 shrink-0 px-1.5 py-0">In flight</Badge>
                    )}
                  </div>
                  <div className="text-[9px] text-slate-500 mt-0.5">
                    {(c.locked_at || c.created_at) ? (c.locked_at || c.created_at).slice(0, 10) : 'Current cycle'}
                    {c.name === activeCohortName && (
                      <span className="ml-1 text-sky-700 font-semibold uppercase">· Active</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────
// StickyKanban — 4-state tile strip that lives inside the sticky
// header. Always visible while the committee scrolls through 27+
// horizontal-scrolling asset cards below.
// ────────────────────────────────────────────────────────────────────
function StickyKanban({ counts, statusFilter, setStatusFilter }) {
  const tileCount = (key) => {
    if (key === 'Pending')      return counts.pending      || 0;
    if (key === 'Under Review') return counts.under_review || counts.shortlisted || 0;
    if (key === 'Scored')       return counts.scored ?? ((counts.approved || 0) + (counts.rejected || 0));
    return 0;
  };
  return (
    <div className="bg-white/95 backdrop-blur-sm border-t border-slate-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-6 py-2">
        <div className="grid grid-cols-3 gap-2" data-testid="sticky-kanban">
          {DECISION_STATES.map((s) => {
            const isActive = statusFilter === s.key;
            return (
              <button
                key={s.key}
                onClick={() => setStatusFilter(isActive ? null : s.key)}
                className={`border rounded-md px-2.5 py-1.5 text-left transition-all ${s.soft} ${isActive ? 'ring-2 ring-offset-1 ring-violet-500 shadow-sm' : 'hover:shadow-sm'}`}
                data-testid={`sticky-kanban-${s.key.toLowerCase().replace(/\s+/g, '-')}`}
                title={s.desc}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <div className="text-[10px] uppercase tracking-wide font-semibold">{s.key}</div>
                  <div className="text-sm font-bold">{tileCount(s.key)}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────
// StagePill — phase-colored badge so the development stage is the
// first thing the committee notices on every card.
// Feb 2026 · user report — the pill was rendering the raw phase
// string (e.g. "FILED (NDA ACCEPTED IN CHINA FOR TYPE 2 DIABETES
// MELLITUS; COMPLETED PHASE 3 FOR T2DM, PHASE 2 FOR MASH)[4][5]…")
// because Claude sometimes writes long prose into the phase field.
// We now extract the leading canonical stage word so the badge stays
// scannable. The full descriptive text still shows in the "Stage:"
// details section right below the pill — no info is lost.
// ────────────────────────────────────────────────────────────────────
const STAGE_COLORS = {
  'Preclinical':  'bg-slate-100 text-slate-800 border-slate-300',
  'IND':          'bg-sky-100 text-sky-800 border-sky-300',
  'Phase 1':      'bg-sky-100 text-sky-800 border-sky-300',
  'Phase 1/2':    'bg-indigo-100 text-indigo-800 border-indigo-300',
  'Phase 2':      'bg-indigo-100 text-indigo-800 border-indigo-300',
  'Phase 2/3':    'bg-violet-100 text-violet-800 border-violet-300',
  'Phase 3':      'bg-violet-100 text-violet-800 border-violet-300',
  'Approved':     'bg-emerald-100 text-emerald-800 border-emerald-300',
  'Marketed':     'bg-emerald-100 text-emerald-800 border-emerald-300',
  'Commercial':   'bg-emerald-100 text-emerald-800 border-emerald-300',
  'Launched':     'bg-emerald-100 text-emerald-800 border-emerald-300',
  'Filed':        'bg-emerald-100 text-emerald-800 border-emerald-300',
  'NDA Filed':    'bg-emerald-100 text-emerald-800 border-emerald-300',
  'BLA Filed':    'bg-emerald-100 text-emerald-800 border-emerald-300',
};

// Feb 2026 · pull the canonical stage word from a possibly-noisy
// input string. Handles "Filed (NDA accepted…)" → "Filed",
// "Phase 3 for T2DM" → "Phase 3", etc.
function _canonicaliseStage(raw) {
  if (!raw) return '';
  const s = String(raw).trim();
  // Cheap first: strip parentheses + citation brackets, keep the rest.
  const cleaned = s.replace(/\s*\([^)]*\)\s*/g, ' ').replace(/\[[^\]]*\]/g, '').trim();
  // Match the leading canonical stage phrase.
  const m = cleaned.match(
    /^(preclinical|ind|phase\s*(?:1\/2|2\/3|1|2|3)|approved|marketed|commercial|launched|nda\s*filed|bla\s*filed|filed)/i,
  );
  if (!m) return '';
  const raw_hit = m[1].replace(/\s+/g, ' ');
  // Title-case with special-casing for the acronym stages.
  const upper = raw_hit.toUpperCase();
  if (['IND', 'NDA FILED', 'BLA FILED'].includes(upper)) return upper.replace('NDA', 'NDA').replace('BLA', 'BLA');
  if (upper.startsWith('PHASE')) return upper.charAt(0) + upper.slice(1).toLowerCase().replace('phase', 'Phase');
  return raw_hit.charAt(0).toUpperCase() + raw_hit.slice(1).toLowerCase();
}

function StagePill({ phase }) {
  const canonical = _canonicaliseStage(phase);
  if (!canonical) return null; // suppress — the descriptive "Stage:" text below covers it
  // Match into the color map (case-insensitive normalise).
  const label = canonical.replace(/^Phase\s*/i, 'Phase ');
  const cls = STAGE_COLORS[label] || 'bg-slate-100 text-slate-700 border-slate-300';
  return (
    <span
      className={`inline-flex items-center text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded border ${cls}`}
      title={`Development stage: ${phase || label}`}
      data-testid="stage-pill"
    >
      {label}
    </span>
  );
}


// ────────────────────────────────────────────────────────────────────
// AIConcernChip — surfaces Claude's contrary verdict when the
// score-derived tier (rubric) disagrees with the qualitative read.
// Math-first tiering (Feb 2026, Option C) means the score governs
// the bucket; this chip exists purely to make AI disagreements
// transparent to the committee — never to override the rubric.
// Hover reveals Claude's verdict, rationale, and key risks.
//
// (Feb 2026 wording refresh) Prior label "AI flagged upgrade" was
// ambiguous — "flagged" reads like a complaint. New copy is
// action-first: "AI elevated → High Priority" / "AI lowered → Not
// Suitable", which reads as an explicit tier override reason.
// ────────────────────────────────────────────────────────────────────
function AIConcernChip(_props) {
  // Feb 2026 · user request "you can remove all these tags where AI
  // elevated / AI lowered". Component preserved (so other files that
  // import it don't break) but renders nothing. The rationale for
  // Claude-vs-math divergence is now surfaced via the universal (i)
  // popover next to the tier badge (WhyRecommendationInfo.jsx).
  return null;
}
// eslint-disable-next-line no-unused-vars
function _AIConcernChipLegacy({ concern, eaId }) {
  if (!concern || !concern.claude_recommendation) return null;
  const isDowngrade = concern.direction === 'downgrade';
  const cls = isDowngrade
    ? 'bg-amber-50 text-amber-800 border-amber-300'
    : 'bg-sky-50 text-sky-800 border-sky-300';
  // Compose a tooltip that explains the disagreement + reasoning. Keeps
  // the chip small in the layout but gives the committee one-hover access
  // to the AI's underlying reasoning.
  const tooltipParts = [
    isDowngrade
      ? `AI lowered this asset from the math tier (${concern.math_recommendation}) down to ${concern.claude_recommendation}.`
      : `AI elevated this asset above the math tier (${concern.math_recommendation}) up to ${concern.claude_recommendation}.`,
    'The score-derived band is what the math says; the AI verdict is a holistic override that factors competition, IP, commercial fit and strategic alignment.',
  ];
  if (concern.rationale) tooltipParts.push(`\nWhy: ${concern.rationale}`);
  if (Array.isArray(concern.key_risks) && concern.key_risks.length > 0) {
    tooltipParts.push(`\nKey risks:\n• ${concern.key_risks.slice(0, 5).join('\n• ')}`);
  }
  return (
    <Badge
      className={`text-[9px] border inline-flex items-center gap-0.5 ${cls}`}
      title={tooltipParts.join('\n')}
      data-testid={`ai-concern-chip-${eaId || ''}`}
    >
      <AlertTriangle className="w-2.5 h-2.5" />
      {isDowngrade ? 'AI lowered' : 'AI elevated'} → {concern.claude_recommendation}
    </Badge>
  );
}


// ────────────────────────────────────────────────────────────────────
// OffStrategyChip (Feb 2026 Fit Screen) — surfaces "why is this
// science-strong asset marked Not Suitable?" when the post-LLM TA
// gate fires. Distinct from AIConcernChip (which handles math-vs-
// Claude divergence). Reads the `off_strategy_reason` string that
// the backend writes onto both the assessment doc and the client
// API payload when TA falls outside Viatris' declared priority list.
// ────────────────────────────────────────────────────────────────────
// OffStrategyChip (Feb 2026 Fit Screen) — DISABLED per user request.
// The chip previously surfaced "Off-stage" / "Off-strategy TA" labels
// next to Not Suitable assets. Users found the terminology confusing
// (some Not Suitable calls are scientific, not fit-screen) so we now
// render nothing. The underlying `off_strategy_reason` is still stored
// on the DB for audit trails but no longer surfaces in the client UI.
function OffStrategyChip(_props) {
  return null;
}


// ────────────────────────────────────────────────────────────────────
// CohortResultsSection — dedicated tab for consolidated per-cohort
// output. Splits into 3 slots so each piece can be rendered as its
// own section on Home (Sep 2026 reorder):
//   slot="matrix"  → "Final Scoring & Recommendations" table
//   slot="exec"    → AI-generated Cohort Executive Summary block
//   slot="reports" → Cohort report files (Excel / PPTX / PDF)
// All 3 share one cohort-results fetch (cached by activeCohort).
// ────────────────────────────────────────────────────────────────────
function CohortResultsSection({ token, activeCohort, cohortsList, slot = 'matrix' }) {
  const [cohort, setCohort] = useState(activeCohort || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Keep dropdown in sync with whatever the rest of the page resolved
  // (e.g., when the user toggles a different cohort in the
  // Reporting Cycles strip above).
  React.useEffect(() => {
    if (activeCohort && !cohort) setCohort(activeCohort);
  }, [activeCohort, cohort]);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = cohort ? { cohort } : {};
      const r = await axios.get(
        `${API}/client/viatris/${token}/cohort-results`,
        { params },
      );
      setData(r.data);
    } catch (e) {
      // Silent — section just renders empty state
      setData(null);
    } finally { setLoading(false); }
  }, [token, cohort]);
  React.useEffect(() => { load(); }, [load]);

  const counts = data?.counts || {};
  const assets = data?.assets || [];
  const roles = data?.reviewer_roles || [];

  // Hero Summary popup (Feb 2026, replaces inline cohort + final
  // executive summary sections). Opens on Verdict-cell click in the
  // matrix and surfaces the asset's AI exec summary + scoring + AI
  // concerns + ForesightRx Opportunity — the canonical view that will
  // drive downstream report generation.
  const [heroAsset, setHeroAsset] = React.useState(null);
  const [rubricOpen, setRubricOpen] = React.useState(false);

  // Per-slot anchor / header copy so each section renders with its own
  // identity on Home, even though they share one data fetch.
  const SLOT_META = {
    matrix: {
      anchor: 'cohort-results',
      eyebrow: 'Final Scoring & Recommendations',
      title: `Cohort Scoring Matrix · ${data?.cohort_name || '—'}`,
    },
    reports: {
      anchor: 'cohort-reports',
      eyebrow: 'Reports',
      title: `Cohort Reports · ${data?.cohort_name || '—'}`,
    },
  };
  const meta = SLOT_META[slot] || SLOT_META.matrix;

  return (
    <section id={meta.anchor} className="scroll-mt-32" data-testid={`cohort-results-${slot}`}>
      <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50/30 to-white">
        <CardContent className="pt-5 space-y-5">
          {/* Header + cohort dropdown */}
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold mb-1 inline-flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" /> {meta.eyebrow}
              </div>
              <h2 className="text-xl font-bold text-slate-800">{meta.title}</h2>
              {slot === 'matrix' && (
                <p className="text-xs text-slate-600 mt-0.5">
                  {counts.total || 0} assets · <strong className="text-emerald-700">{counts.scored || 0} fully scored</strong> · {counts.under_review || 0} under review · {counts.pending || 0} pending
                </p>
              )}
              {slot === 'matrix' && (
                // Score-band legend — the Viatris team asked for tier
                // transparency. The important nuance (Feb 2026):
                // tier labels are AI-holistic, NOT purely math bands.
                // Claude reads the full asset (IP, competition,
                // commercial fit, modality alignment) and can override
                // the math tier up or down. The (i) chip on each row of
                // the Reference Library / matrix exposes Claude's
                // specific rationale where the display tier differs.
                // (Feb 2026 — user request 1 Jul 2026) The math-anchor
                // legend + "See scoring rubric" moved OUT of this header
                // and INTO each Review Queue card so reviewers see the
                // framing inline with the asset they're evaluating.
                <div className="mt-2 text-[10px] text-slate-500 italic">
                  Tier labels are AI-holistic; math anchors are shown next to each Review Queue card above.
                </div>
              )}
            </div>
            {slot === 'matrix' && cohortsList && cohortsList.length > 1 && (
              <select
                value={cohort}
                onChange={(e) => setCohort(e.target.value)}
                className="text-xs border border-slate-300 rounded px-2.5 py-1.5 bg-white"
                data-testid="cohort-results-picker"
              >
                {cohortsList.map((c) => (
                  <option key={c.cohort_name || c.name} value={c.cohort_name || c.name}>
                    {c.cohort_name || c.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
            </div>
          ) : (
            <>
              {/* Slot: Final Scoring & Recommendations matrix */}
              {slot === 'matrix' && (
              <>
              {/* Scoring Matrix — assets × 6 reviewers.
                  Status column removed (Feb 2026 · user cleanup) —
                  Verdict already surfaces Pending / Under Review /
                  In-License / Reassess / Decline off the same
                  derived_status, so a separate Status badge was pure
                  duplication. */}
              <div className="bg-white rounded border-2 border-slate-300 overflow-x-auto shadow-sm" data-testid="cohort-scoring-matrix">
                <table className="w-full text-xs border-collapse table-fixed [&_th]:border [&_th]:border-slate-300 [&_td]:border [&_td]:border-slate-300">
                  <colgroup>
                    <col className="w-[28%]" />{/* Asset */}
                    <col className="w-[10%]" />{/* Stage */}
                    <col className="w-[5%]"  />{/* MED */}
                    <col className="w-[5%]"  />{/* CLIN */}
                    <col className="w-[5%]"  />{/* REG */}
                    <col className="w-[5%]"  />{/* PV */}
                    <col className="w-[5%]"  />{/* BD */}
                    <col className="w-[5%]"  />{/* RES */}
                    <col className="w-[10%]" />{/* Avg /5 */}
                    <col className="w-[22%]" />{/* Verdict */}
                  </colgroup>
                  <thead className="bg-slate-100 text-[10px] uppercase tracking-wide text-slate-700">
                    <tr>
                      <th className="px-3 py-2 text-left">Asset</th>
                      <th className="px-2 py-2 text-center">Stage</th>
                      {roles.map((r) => (
                        <th key={r.id} className="px-2 py-2 text-center" title={r.label}>{r.short}</th>
                      ))}
                      <th className="px-2 py-2 text-center"><span className="inline-flex items-center gap-1 justify-center">Avg /5 <DefinitionInfo defKey="avg5" /></span></th>
                      <th className="px-2 py-2 text-left">Verdict</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assets.length === 0 ? (
                      <tr><td colSpan={roles.length + 4} className="text-center py-10 text-slate-400 italic">
                        No active-review assets in this cohort yet.
                      </td></tr>
                    ) : assets.map((a) => {
                      const allGreen = (a.members_submitted ?? a.members_scored) === a.total_members;
                      const pendingRoles = (a.members || []).filter((m) => !m.submitted).map((m) => m.short);
                      return (
                      <tr key={a.ea_id} className="hover:bg-slate-50/60" data-testid={`cohort-results-row-${a.ea_id}`}>
                        <td className="px-3 py-2 align-top">
                          <div className="font-semibold text-slate-800 leading-tight break-words">{a.asset_name}</div>
                          <div className="text-[10px] text-slate-500 line-clamp-2" title={a.indication || ''}>
                            {a.indication || '—'}
                          </div>
                          {/* AI Concern + Off-Strategy chips removed (Feb 2026 · user request) —
                              already surfaced on the Reference Library rows so the Cohort
                              Scoring Matrix stays focused on the scoring signal. */}
                        </td>
                        <td className="px-2 py-2 text-center text-[10px] text-slate-600 align-middle">{a.phase || '—'}</td>
                        {a.members.map((m) => (
                          <td key={m.member_id} className="px-2 py-2 text-center align-middle">
                            {m.submitted ? (
                              <span
                                className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-emerald-500 text-white text-[9px] font-bold"
                                title={`${m.label}: scored${m.fully_scored ? '' : ' (partial)'}`}
                              >
                                ✓
                              </span>
                            ) : (
                              <span
                                className="inline-block w-3 h-3 rounded-full bg-orange-400"
                                title={`${m.label}: scoring pending`}
                              />
                            )}
                          </td>
                        ))}
                        <td className="px-2 py-2 text-center font-mono text-slate-700 align-middle">
                          {allGreen && a.committee_overall_avg != null
                            ? <span className="text-emerald-700 font-bold">{a.committee_overall_avg}</span>
                            : <Lock className="w-3 h-3 inline text-slate-300" />}
                        </td>
                        <td className="px-2 py-2 text-left align-middle">
                          <VerdictCell
                            asset={a}
                            allGreen={allGreen}
                            pendingRoles={pendingRoles}
                            onOpen={() => setHeroAsset(a)}
                          />
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="text-[10px] text-slate-600 flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-orange-400 inline-block" /> Scoring pending</span>
                <span className="inline-flex items-center gap-1"><span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-emerald-500 text-white text-[8px] font-bold">✓</span> Scored</span>
                <span className="inline-flex items-center gap-1"><Lock className="w-3 h-3 text-slate-300" /> Hidden until all 6 in · 🔓 unlocks the committee weighted avg <DefinitionInfo defKey="lock" /></span>
              </div>
              </>
              )}

              {/* Slot: Cohort Executive Summary (REMOVED Feb 2026).
                  The asset-level Hero Summary popup (opens on Verdict
                  cell click) now consolidates AI exec summary +
                  scoring + AI concerns + ForesightRx Opportunity per
                  asset — the canonical surface that will drive report
                  generation. The cohort-level Claude narrative no
                  longer renders inline. */}

              {/* Slot: Cohort Reports */}
              {slot === 'reports' && (
              <>
              {(data?.files || []).length > 0 ? (
                <div className="bg-white rounded border border-slate-200 p-4" data-testid="cohort-results-files">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-3">Reports for this cohort</div>
                  <ul className="space-y-2">
                    {data.files.map((f) => (
                      <li
                        key={f.id || f.file_id}
                        className="flex items-center justify-between gap-3 text-xs py-1.5"
                      >
                        <span className="text-slate-700 truncate flex-1 min-w-0">{f.label || f.filename}</span>
                        {/* Icon download button — restores the pre-Sep
                            2026 styling. Pill-shaped, indigo-tinted,
                            with the lucide Download icon. */}
                        <a
                          href={`${API}/client/viatris/${token}/cohort-files/${f.id || f.file_id}/download`}
                          className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-700 hover:text-indigo-900 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-full px-3 py-1.5 transition-colors whitespace-nowrap shrink-0"
                          data-testid={`cohort-results-download-${f.id || f.file_id}`}
                        >
                          <Download className="w-3.5 h-3.5" />
                          Download
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="text-xs text-slate-500 italic" data-testid="cohort-reports-pending">
                  Reports for this cohort will appear here once the ForesightRx team publishes them.
                </p>
              )}
              </>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Executive Summary popup — synthesised board-memo brief
          (Feb 2026). Renders ONLY when the committee has all 6
          scores in (the Verdict cell gates the click). Backend
          caches the synthesis by input-hash; force-regenerate is
          available inside the modal. */}
      {heroAsset && (
        <ExecutiveSummaryModal
          token={token}
          asset={heroAsset}
          onClose={() => setHeroAsset(null)}
        />
      )}

      {/* Scoring Rubric reference (Feb 2026). Opened from any of the
          "See scoring rubric" links across the portal — Cohort Scoring
          Matrix legend, Deep Dive modal, Executive Summary modal. */}
      {rubricOpen && (
        <ScoringRubricModal onClose={() => setRubricOpen(false)} />
      )}
    </section>
  );
}


// VerdictCell — the cell in the Cohort Scoring Matrix that flips
// through the committee lifecycle:
//   • 0 / 6 scored        → "Pending"  (slate)
//   • 1–5 / 6 scored      → "Under Review"  (amber)
//   • 6 / 6 scored        → derived verdict: "In-License" / "Reassess" / "Decline"
// The "Executive Summary →" affordance only LIGHTS UP once all 6
// reviewers submit — the synthesis can't exist before every decision
// is in, so making it clickable pre-committee would surface an empty
// or misleading view. Pre-committee shows a disabled "Executive
// Summary · awaiting all scores" line as a gentle preview of what
// will appear post-decision.
function VerdictCell({ asset, allGreen, pendingRoles, onOpen }) {
  // Feb 2026 — Verdict cell simplification (user request 1 Jul 2026).
  // The badge alone carries the state (Pending / Under Review / In-License /
  // Reassess / Decline). No secondary "N of 6 still to score" line, no
  // "Lock · awaiting all scores" line — those duplicated what the badge
  // already says. The whole cell becomes a click target that opens the
  // Executive Summary ONLY when the verdict is final (all 6 in).
  const v = allGreen ? deriveVerdict(asset.committee_overall_avg) : null;
  const someScored = (asset.members_submitted_count || asset.members_scored_count || 0) > 0;
  const finalBadge = v
    ? v
    : someScored
      ? { word: 'Under Review', cls: 'bg-amber-100 text-amber-900 border-amber-300' }
      : { word: 'Pending', cls: 'bg-slate-100 text-slate-700 border-slate-300' };
  const summaryReady = allGreen;
  return (
    <button
      type="button"
      onClick={summaryReady ? onOpen : undefined}
      disabled={!summaryReady}
      className={`text-left w-full rounded px-1 py-0.5 transition-colors ${
        summaryReady
          ? 'hover:bg-slate-50 cursor-pointer group'
          : 'cursor-default'
      }`}
      data-testid={`verdict-cell-${asset.ea_id}`}
      title={summaryReady
        ? 'Open the synthesised Executive Summary'
        : (finalBadge.word === 'Under Review'
           ? `${pendingRoles.length} of 6 still to score — verdict unlocks when all 6 submit`
           : 'Verdict will auto-derive once the committee has submitted scores')}
    >
      <span className="inline-flex items-center gap-1">
        <Badge className={`text-[11px] border font-semibold ${finalBadge.cls}`}>
          {finalBadge.word}
        </Badge>
        {/* Feb 2026 — verdict definition popover: explains all 5 states
            (Pending / Under Review / In-License / Reassess / Decline) +
            the thresholds. Wrapped in a click-swallow span so the parent
            button's onClick (opens Executive Summary) doesn't fire. */}
        <span onClick={(e) => e.stopPropagation()} className="inline-flex">
          <DefinitionInfo defKey="verdict" />
        </span>
      </span>
      {summaryReady && (
        <span className="text-indigo-700 group-hover:underline text-[10px] font-medium inline-flex items-center gap-0.5 ml-1.5">
          Exec Summary <ChevronRight className="w-2.5 h-2.5" />
        </span>
      )}
    </button>
  );
}


// deriveVerdict — math-first verdict derivation from the committee
// weighted average on the /5 scale. (Feb 2026 policy shift) FRx and
// Overall /50 have been retired from the client-visible portal; the
// verdict fires off the committee weighted mean alone.
//   ≥ 4.0 → In-License · ≥ 3.0 → Reassess · < 3.0 → Decline
const VERDICT_THRESHOLDS = { recommend: 4.0, accept: 3.0 };

// Maps a tier label → glossary key in viatrisDefinitions.js. Centralised
// so the same tier badge popover renders consistently anywhere we surface
// it (Review Card, Matrix rows, Reference Library, Deep Dive).
function tierDefKey(tier) {
  switch (tier) {
    case 'Strategic Priority': return 'tier_strategic';
    case 'High Priority':      return 'tier_high';
    case 'Medium':             return 'tier_medium';
    case 'Monitor':            return 'tier_monitor';
    case 'Low Priority':       return 'tier_low';
    case 'Not Suitable':       return 'tier_not_suitable';
    case 'Could Not Assess':   return 'tier_could_not_assess';
    default:                   return 'tier_monitor';
  }
}

// Feb 2026 — Verdict words map to distinct downstream BD actions:
// In-License (board-ready, move to term sheet), Reassess (hold for
// next milestone), Decline (pass). Thresholds (Feb 2026):
// Committee weighted avg ≥ 4.0 → In-License · ≥ 3.0 → Reassess · < 3.0 → Decline.
function deriveVerdict(avg) {
  if (avg == null || isNaN(avg)) return null;
  if (avg >= VERDICT_THRESHOLDS.recommend) {
    return { word: 'In-License', cls: 'bg-emerald-100 text-emerald-900 border-emerald-300' };
  }
  if (avg >= VERDICT_THRESHOLDS.accept) {
    return { word: 'Reassess', cls: 'bg-sky-100 text-sky-900 border-sky-300' };
  }
  return { word: 'Decline', cls: 'bg-rose-100 text-rose-900 border-rose-300' };
}


// ────────────────────────────────────────────────────────────────────
// DeepDiveModal — surfaces the full assessment for an active-review
// asset: executive summary, per-dimension scoring with rationales,
// company intelligence, asset intelligence, scientific assessment,
// competitive landscape, and references. Tabbed for scannability.
// ────────────────────────────────────────────────────────────────────
function DeepDiveModal({ token, asset, onClose }) {
  const [rubricOpen, setRubricOpen] = React.useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('exec');
  // Highlight state for the right-rail Reference Library — updated
  // whenever a [N] chip is clicked in prose or a per-section ref strip.
  const [highlightN, setHighlightN] = useState(null);
  const onCite = (n) => setHighlightN(n);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/client/viatris/${token}/assets/${asset.ea_id}/deep-dive`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Failed to load deep dive');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, asset.ea_id]);

  // Reference tab removed (Feb 2026): the persistent right-rail
  // ReferenceRail replaces it. Every section now shows its own
  // "References cited here" chip strip inline.
  // Latest Alerts tab also removed (Feb 2026, client request) — alerts
  // remain accessible from the main dashboard "Latest Intelligence" surface.
  const TABS = [
    { key: 'exec',    label: 'Executive Summary' },
    { key: 'scoring', label: 'Scoring' },
    { key: 'company', label: 'Company' },
    { key: 'asset',   label: 'Asset' },
    { key: 'science', label: 'Science' },
    { key: 'comp',    label: 'Competition' },
  ];

  // Deep-dive payload adds Feb 2026 fields:
  //   references_by_section: { executive_summary: [1,3], company_intelligence: [2,4], ... }
  //   references:            [{ n, url, title }, ...] — always normalised
  const refs = Array.isArray(data?.references) ? data.references : [];
  const refsBySection = data?.references_by_section || {};
  const maxRef = refs.length;

  return (
    <CitationModeContext.Provider value={CITATION_MODES.BIBLIOGRAPHY}>
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      {rubricOpen && <ScoringRubricModal onClose={() => setRubricOpen(false)} />}
      <div
        className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="deep-dive-modal"
      >
        {/* Header */}
        <div className="px-5 py-3 border-b border-slate-200 flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <StagePill phase={asset.phase} />
              <h3 className="font-bold text-lg text-slate-800 truncate" title={asset.asset_name}>{asset.asset_name}</h3>
              {data?.composite_score != null && (
                <span
                  className="inline-flex items-baseline gap-0.5 text-xs px-2 py-0.5 rounded bg-indigo-50 border border-indigo-200 text-indigo-800"
                  title="Weighted average per scored dimension (1–5 scale)"
                >
                  <span className="font-bold">{(data.weighted_avg_score ?? data.composite_score / 9).toFixed(2)}</span>
                  <span className="text-[10px] opacity-70">/5</span>
                </span>
              )}
              {data?.overall_recommendation?.state && (
                <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-800 bg-emerald-50">
                  {data.overall_recommendation.state}
                </Badge>
              )}
              {data?.recommendation && !data?.overall_recommendation?.state && (
                <Badge variant="outline" className="text-[10px]">{data.recommendation}</Badge>
              )}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              {asset.indication} · {asset.modality} · {asset.biotech_name}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 p-1"
            data-testid="deep-dive-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-5 pt-3 border-b border-slate-200 flex gap-1 flex-wrap">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`text-xs px-3 py-1.5 rounded-t border-b-2 transition-colors ${
                tab === t.key
                  ? 'border-indigo-600 text-indigo-700 font-semibold'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              data-testid={`deep-dive-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Body — 2-column layout: content (left) + Reference Rail (right).
            The rail is persistent (visible on every tab except Scoring),
            per Feb 2026 client request. On narrow viewports (< md) we
            collapse the rail below the content instead of side-by-side. */}
        <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
          <div className="flex-1 overflow-y-auto p-5 text-sm min-w-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
              </div>
            ) : error ? (
              <div className="text-rose-700 text-sm">{error}</div>
            ) : !data?.has_assessment ? (
              <div className="text-slate-500 text-sm">
                {data?.message || 'No assessment available for this asset yet.'}
              </div>
            ) : (
              <>
                {tab === 'exec'    && <DeepDiveExec    exec={data.executive_summary} refIds={refsBySection.executive_summary} onCite={onCite} maxRef={maxRef} />}
                {tab === 'scoring' && <DeepDiveScoring scoring={data.scoring} composite={data.composite_score} weighted={data.weighted_avg_score} />}
                {tab === 'company' && <CompanyProfileBlock data={data.company_intelligence}  refIds={refsBySection.company_intelligence}  onCite={onCite} maxRef={maxRef} />}
                {tab === 'asset'   && <GenericSection title="Asset Intelligence"    data={data.asset_intelligence}    refIds={refsBySection.asset_intelligence}    onCite={onCite} maxRef={maxRef} />}
                {tab === 'science' && <GenericSection title="Scientific Assessment" data={data.scientific_assessment} refIds={refsBySection.scientific_assessment} onCite={onCite} maxRef={maxRef} />}
                {tab === 'comp'    && <CompetitionBlock data={data.competitive_landscape} refIds={refsBySection.competitive_landscape} onCite={onCite} maxRef={maxRef} />}
              </>
            )}
          </div>
          {/* Right rail — hidden on the scoring tab (score dims are
              rubric-based, not citation-based) and when there are no
              references at all. */}
          {!loading && data?.has_assessment && tab !== 'scoring' && refs.length > 0 && (
            <div className="md:w-80 md:shrink-0 max-h-[45vh] md:max-h-none overflow-hidden md:border-t-0 border-t border-slate-200">
              <ReferenceRail
                refs={refs}
                highlightN={highlightN}
                onDismissHighlight={() => setHighlightN(null)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
    </CitationModeContext.Provider>
  );
}

function DeepDiveOverall({ data }) {
  // Read-only Overall Assessment surface for the Viatris committee.
  // (Feb 2026 policy shift) FRx Opportunity Assessment has been retired
  // from the client-visible portal — the committee sees only the
  // Scientific Evaluation weighted average and the derived verdict.
  const weighted = data?.weighted_avg_score ?? (
    data?.composite_score != null ? data.composite_score / 9 : null
  );
  const rec = data?.overall_recommendation;
  const STATE_COLORS = {
    'Approve for Licensing Evaluation': 'bg-emerald-100 text-emerald-900 border-emerald-300',
    'Proceed to Deep Dive':             'bg-violet-100 text-violet-900 border-violet-300',
    'Monitor':                          'bg-amber-100 text-amber-900 border-amber-300',
    'Reassess After Milestone':         'bg-sky-100 text-sky-900 border-sky-300',
    'Verify Rights Availability':       'bg-orange-100 text-orange-900 border-orange-300',
    'Reject':                           'bg-rose-100 text-rose-900 border-rose-300',
  };
  return (
    <div className="space-y-4">
      {/* Hero — single Scientific Evaluation weighted score + derived verdict */}
      <div className="p-4 rounded-md border border-emerald-200 bg-gradient-to-br from-emerald-50/60 to-indigo-50/30" data-testid="overall-hero-score">
        <div className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold">
          Scientific Evaluation · Committee Weighted Average
        </div>
        <div className="flex items-baseline gap-3 mt-1">
          <div className="text-4xl font-bold text-emerald-800">
            {weighted != null ? weighted.toFixed(2) : '—'}
            <span className="text-base font-normal text-slate-500"> / 5</span>
          </div>
          {(() => {
            const v = deriveVerdict(weighted);
            if (!v) return null;
            return (
              <Badge
                className={`text-sm border font-bold ${v.cls}`}
                data-testid="hero-verdict-badge"
                title={`Committee weighted avg ≥ ${VERDICT_THRESHOLDS.recommend} → In-License · ≥ ${VERDICT_THRESHOLDS.accept} → Reassess · < ${VERDICT_THRESHOLDS.accept} → Decline`}
              >
                Verdict: {v.word}
              </Badge>
            );
          })()}
        </div>
        <div className="text-[11px] text-slate-600 mt-1.5">
          Mean over every scored 1–5 vote across the 9 dimensions and 6 committee reviewers
          (partial submissions included). Verdict thresholds: ≥ {VERDICT_THRESHOLDS.recommend} In-License · ≥ {VERDICT_THRESHOLDS.accept} Reassess · &lt; {VERDICT_THRESHOLDS.accept} Decline.
        </div>
      </div>

      {/* Overall Recommendation panel — Claude-generated 6-state verdict */}
      {rec ? (
        <div className="p-4 rounded-md border border-emerald-200 bg-gradient-to-br from-emerald-50/50 to-indigo-50/30" data-testid="overall-recommendation-readonly">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <Sparkles className="w-4 h-4 text-emerald-700" />
            <span className="text-[10px] uppercase tracking-wide text-emerald-800 font-semibold">
              Overall Recommendation
            </span>
            <Badge className={`text-sm border ${STATE_COLORS[rec.state] || 'bg-slate-100 text-slate-800 border-slate-300'}`}>
              {rec.state}
            </Badge>
            {rec.generated_at && (
              <span className="text-[10px] text-slate-500 ml-auto">
                Generated {(rec.generated_at || '').slice(0, 10)}
              </span>
            )}
          </div>
          {rec.rationale && (
            <p className="text-sm text-slate-700 leading-relaxed mb-2.5">{rec.rationale}</p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
            {(rec.key_drivers || []).length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold mb-1">Key Drivers</div>
                <ul className="space-y-0.5 list-disc pl-4 text-slate-700">
                  {(rec.key_drivers || []).map((x, i) => <li key={i}>{x}</li>)}
                </ul>
              </div>
            )}
            {(rec.key_risks || []).length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wide text-rose-700 font-semibold mb-1">Key Risks</div>
                <ul className="space-y-0.5 list-disc pl-4 text-slate-700">
                  {(rec.key_risks || []).map((x, i) => <li key={i}>{x}</li>)}
                </ul>
              </div>
            )}
            {(rec.suggested_next_steps || []).length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wide text-indigo-700 font-semibold mb-1">Suggested Next Steps</div>
                <ul className="space-y-0.5 list-disc pl-4 text-slate-700">
                  {(rec.suggested_next_steps || []).map((x, i) => <li key={i}>{x}</li>)}
                </ul>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="p-3 rounded-md border border-dashed border-slate-300 bg-slate-50 text-xs text-slate-600">
          The ForesightRx team has not yet generated an Overall Recommendation for this asset. It
          will appear here once the Scientific Evaluation has been consolidated by the analyst team.
        </div>
      )}

      {/* Feb 2026 — FRx Opportunity Rationale block removed from the
          client-visible portal (FRx retired). The rationale remains
          available to the internal FRx analyst team via the Workbench. */}
    </div>
  );
}


function DeepDiveExec({ exec, refIds, onCite, maxRef }) {
  if (!exec || !Object.keys(exec).length) {
    return <div className="text-slate-500 text-sm">No executive summary available.</div>;
  }
  return (
    <div className="space-y-4">
      {exec.headline && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Headline</div>
          <p className="text-base text-slate-800 italic">
            &ldquo;<CitedText text={exec.headline} onCite={onCite} maxRef={maxRef} />&rdquo;
          </p>
        </div>
      )}
      {exec.rationale && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Rationale</div>
          <p className="text-sm text-slate-700 leading-relaxed">
            <CitedText text={exec.rationale} onCite={onCite} maxRef={maxRef} />
          </p>
        </div>
      )}
      {exec.why_relevant && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Why Relevant</div>
          <p className="text-sm text-slate-700 leading-relaxed">
            <CitedText text={exec.why_relevant} onCite={onCite} maxRef={maxRef} />
          </p>
        </div>
      )}
      {Array.isArray(exec.key_strengths) && exec.key_strengths.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold mb-1">Key Strengths</div>
          <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">
            {exec.key_strengths.map((s, i) => (
              <li key={i}><CitedText text={s} onCite={onCite} maxRef={maxRef} /></li>
            ))}
          </ul>
        </div>
      )}
      {Array.isArray(exec.key_risks || exec.risks) && (exec.key_risks || exec.risks).length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-rose-700 font-semibold mb-1">Key Risks</div>
          <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">
            {(exec.key_risks || exec.risks).map((s, i) => (
              <li key={i}><CitedText text={s} onCite={onCite} maxRef={maxRef} /></li>
            ))}
          </ul>
        </div>
      )}
      {exec.scientific_differentiation && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Scientific Differentiation</div>
          <p className="text-sm text-slate-700 leading-relaxed">
            <CitedText text={exec.scientific_differentiation} onCite={onCite} maxRef={maxRef} />
          </p>
        </div>
      )}
      {exec.commercial_opportunity && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Commercial Opportunity</div>
          <p className="text-sm text-slate-700 leading-relaxed">
            <CitedText text={exec.commercial_opportunity} onCite={onCite} maxRef={maxRef} />
          </p>
        </div>
      )}
      {exec.strategic_fit_for_viatris && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Strategic Fit for Viatris</div>
          <p className="text-sm text-slate-700 leading-relaxed">
            <CitedText text={exec.strategic_fit_for_viatris} onCite={onCite} maxRef={maxRef} />
          </p>
        </div>
      )}
      {exec.recommended_next_action && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-indigo-700 font-semibold mb-1">Recommended Next Action</div>
          <p className="text-sm text-slate-700">
            {exec.recommended_next_action}
            {exec.next_action_rationale && (
              <span className="block text-xs text-slate-500 mt-1">
                <CitedText text={exec.next_action_rationale} onCite={onCite} maxRef={maxRef} />
              </span>
            )}
          </p>
        </div>
      )}
      <SectionRefStrip refIds={refIds} onCite={onCite} />
    </div>
  );
}

function DeepDiveScoring({ scoring, composite, weighted }) {
  if (!scoring || !Object.keys(scoring).length) {
    return <div className="text-slate-500 text-sm">No scoring available.</div>;
  }
  // scoring shape: { dimension_key: {score: 1-5, rationale, confidence}, ... }
  const dims = Object.entries(scoring).filter(([, v]) => v && typeof v === 'object' && 'score' in v);
  return (
    <div className="space-y-3">
      <div className="flex items-baseline gap-4 mb-2">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold inline-flex items-center gap-1">
            Weighted Avg <DefinitionInfo defKey="scoring_section" />
          </div>
          <div className="text-2xl font-bold text-indigo-700">
            {weighted != null ? weighted.toFixed(2) : (composite != null ? (composite / 9).toFixed(2) : '—')}<span className="text-sm font-normal text-slate-500"> / 5</span>
          </div>
        </div>
      </div>
      <div className="space-y-2">
        {dims.map(([key, val], idx) => {
          const score = Number(val.score) || 0;
          const pct = (score / 5) * 100;
          return (
            <div key={key} className="border border-slate-100 rounded p-3 bg-slate-50/40">
              <div className="flex items-center justify-between gap-3 mb-1">
                <div className="text-xs font-semibold text-slate-800 capitalize inline-flex items-center gap-1.5">
                  <span className="text-[10px] font-mono text-slate-400 tabular-nums">{idx + 1}.</span>
                  {key.replace(/_/g, ' ')}
                </div>
                <div className="text-sm font-bold text-indigo-700">{score}<span className="text-[10px] font-normal text-slate-500"> / 5</span></div>
              </div>
              <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden mb-2">
                <div className="h-full bg-indigo-500" style={{ width: `${pct}%` }} />
              </div>
              {val.rationale && (
                <p className="text-[11px] text-slate-700 leading-relaxed">{val.rationale}</p>
              )}
              {val.confidence && (
                <div className="text-[10px] text-slate-500 mt-1">Confidence: <strong>{val.confidence}</strong></div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// DeepDiveSection + renderValue moved to /lib/deepDiveRender.js (Feb 2026)
// Replaced with GenericSection + CompanyProfileBlock + CompetitionBlock imports.


// ────────────────────────────────────────────────────────────────────
// DeepDiveCommittee — 6-member committee scoring matrix.
// Rows = 9 scoring dimensions. Columns = AI score · Member 1..6 · Avg.
// Each cell for Members 1–6 is editable; submitting saves to the
// backend and triggers re-aggregation. Designed to live alongside the
// AI Scoring tab so reviewers see both signals in context.
// ────────────────────────────────────────────────────────────────────
// Sep 2026 architecture clarification — Scientific Evaluation is back
// to 9 dimensions / 45 max (the client's original methodology stays
// unchanged). Licensing feasibility is captured SEPARATELY as the
// ForesightRx Opportunity Assessment (/5, internal-only). Overall
// Assessment = Scientific (45) + Opportunity (5) = 50.
const SCORING_DIMS = [
  'prevalence', 'unmet_need', 'efficacy', 'safety',
  'competitive_differentiation', 'time_to_launch', 'strategic_alignment',
  'remaining_investment', 'clinical_adoption',
];
const DIM_LABELS = {
  prevalence:                  'Prevalence',
  unmet_need:                  'Unmet Need',
  efficacy:                    'Efficacy',
  safety:                      'Safety',
  competitive_differentiation: 'Competitive Differentiation',
  time_to_launch:              'Time to Launch',
  strategic_alignment:         'Strategic Alignment',
  remaining_investment:        'Remaining Investment Required',
  clinical_adoption:           'Clinical Adoption Potential',
};
const SCIENTIFIC_MAX = SCORING_DIMS.length * 5; // = 45
const OPPORTUNITY_MAX = 5;
const OVERALL_MAX = SCIENTIFIC_MAX + OPPORTUNITY_MAX; // = 50

// Skip sentinel — reviewers may opt out of scoring any single dimension.
// Skips count as an answered decision (so the reviewer is "in") but do
// NOT contribute to any numeric average — mirrors backend logic in
// `viatris_client.py::SKIP_VALUE`.
const SKIP = 'skip';
const _isNum = (v) => typeof v === 'number';
const _isSkip = (v) => v === SKIP;
const _hasAnswer = (v) => _isNum(v) || _isSkip(v);   // used for "N of 9 answered"
const _parseScoreInput = (raw) => {
  if (raw === '' || raw == null) return undefined;
  if (raw === SKIP) return SKIP;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
};

// Named reviewer roles — enforced 1 per role (committee size = 6).
const REVIEWER_ROLES = [
  { id: 'role-medical',          label: 'Medical',                    short: 'MED' },
  { id: 'role-clinical',         label: 'Clinical',                   short: 'CLIN' },
  { id: 'role-regulatory',       label: 'Regulatory Affairs',         short: 'REG' },
  { id: 'role-pharmacovigilance',label: 'Pharmacovigilance',          short: 'PV' },
  { id: 'role-bd',               label: 'Business Development / S&E', short: 'BD' },
  { id: 'role-reserve',          label: 'Reserve',                    short: 'RES' },
];
const MEMBER_IDS = REVIEWER_ROLES.map((r) => r.id);
const ROLE_LABEL = Object.fromEntries(REVIEWER_ROLES.map((r) => [r.id, r.label]));
const ROLE_SHORT = Object.fromEntries(REVIEWER_ROLES.map((r) => [r.id, r.short]));

function CommitteeScoringMatrixSection({ token, activeAssets }) {
  // Inline committee scoring panel — lives on the main dashboard so
  // reviewers don't need to open the Deep Dive modal per asset. Pick
  // an asset from the dropdown and the same blinded scoring matrix
  // renders directly on the page.
  const list = activeAssets || [];
  const [selectedId, setSelectedId] = useState(list[0]?.ea_id || null);
  const [aiScoring, setAiScoring] = useState(null);
  const [loadingAi, setLoadingAi] = useState(false);

  // Keep selection alive across re-renders; default to first asset
  // whenever the active list changes (e.g. on first load).
  React.useEffect(() => {
    if (!selectedId && list.length) setSelectedId(list[0].ea_id);
    if (selectedId && !list.find((a) => a.ea_id === selectedId) && list.length) {
      setSelectedId(list[0].ea_id);
    }
  }, [list, selectedId]);

  // Fetch AI scoring for the selected asset so the "AI" column on the
  // matrix is populated. The deep-dive endpoint returns the full
  // assessment incl. the 9-domain scoring dict.
  React.useEffect(() => {
    if (!selectedId) { setAiScoring(null); return; }
    let cancelled = false;
    (async () => {
      setLoadingAi(true);
      try {
        const r = await axios.get(`${API}/client/viatris/${token}/assets/${selectedId}/deep-dive`);
        if (!cancelled) setAiScoring(r.data?.scoring || null);
      } catch (e) {
        if (!cancelled) setAiScoring(null);
      } finally {
        if (!cancelled) setLoadingAi(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, selectedId]);

  if (!list.length) return null;
  const selected = list.find((a) => a.ea_id === selectedId) || list[0];

  return (
    <section id="committee-scoring" className="scroll-mt-32" data-testid="committee-scoring-matrix">
      <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50/30 to-violet-50/20">
        <CardContent className="pt-5 space-y-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-indigo-700 font-semibold mb-1 inline-flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" /> Committee Scoring Matrix
              </div>
              <h2 className="text-xl font-bold text-slate-800">Score an asset inline</h2>
              <p className="text-xs text-slate-600 mt-0.5">
                Pick an asset, enter your 1–5 score across all 9 Scientific Evaluation domains.
                Other reviewers&rsquo; columns reveal only after you submit your own.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Asset</span>
              <select
                value={selectedId || ''}
                onChange={(e) => setSelectedId(e.target.value)}
                className="text-xs border border-indigo-300 rounded px-2 py-1.5 bg-white min-w-[260px] focus:outline-none focus:border-indigo-500"
                data-testid="committee-scoring-asset-select"
              >
                {list.map((a) => (
                  <option key={a.ea_id} value={a.ea_id}>
                    {a.asset_name} · {a.indication || '—'} · {a.derived_status || a.client_decision || 'Pending'}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Asset context strip so reviewer always knows what they're scoring */}
          <div className="flex items-center gap-3 flex-wrap text-xs bg-white/70 border border-slate-200 rounded px-3 py-2">
            <span className="font-semibold text-slate-900">{selected.asset_name}</span>
            <span className="text-slate-400">·</span>
            <span className="text-slate-700">{selected.indication || '—'}</span>
            <span className="text-slate-400">·</span>
            <span className="text-slate-700">{selected.phase || '—'}</span>
            <span className="text-slate-400">·</span>
            <span className="text-slate-700">{selected.therapeutic_area || '—'}</span>
            {selected.composite_score != null && (
              <>
                <span className="text-slate-400">·</span>
                <span className="text-slate-700">Weighted <strong>{(selected.weighted_avg_score ?? selected.composite_score / 9).toFixed(2)} / 5</strong></span>
              </>
            )}
            {selected.recommendation && (
              <Badge className={`text-[10px] ${DECISION_BADGE[selected.derived_status] || 'bg-slate-100 text-slate-700'}`}>
                {selected.recommendation}
              </Badge>
            )}
          </div>

          {/* The matrix itself — reuse DeepDiveCommittee verbatim so the
              blinded scoring logic + consensus stats stay in one place. */}
          {loadingAi ? (
            <div className="text-xs text-slate-500 flex items-center gap-2 py-3">
              <Loader2 className="w-3 h-3 animate-spin" /> Loading AI scoring…
            </div>
          ) : (
            <DeepDiveCommittee token={token} eaId={selected.ea_id} aiScoring={aiScoring} />
          )}
        </CardContent>
      </Card>
    </section>
  );
}


function DeepDiveCommittee({ token, eaId, aiScoring }) {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  // Blinded identity (Jun 2026): each committee member self-selects
  // their slot once per browser. The selection is cached in
  // localStorage so a quick refresh doesn't lose their identity, but
  // they can always switch via the "Change member" link. Until the
  // current member has SUBMITTED all 9 dimensions, the other five
  // members' columns + the committee average stay hidden — Delphi-
  // style anti-anchoring. This is good-faith blinding until the
  // formal JWT-per-member auth ships (P1 backlog).
  const [myMemberId, setMyMemberId] = useState(() => {
    try { return localStorage.getItem('viatris.member_id') || null; }
    catch { return null; }
  });

  const load = React.useCallback(async () => {
    try {
      // Send `as=member-X` so the backend enforces blinding server-side
      // (strips other members' rows + committee_avg until I've scored).
      const q = myMemberId ? `?as=${encodeURIComponent(myMemberId)}` : '';
      const r = await axios.get(`${API}/client/viatris/${token}/assets/${eaId}/member-scores${q}`);
      setData(r.data);
    } catch (e) {
      // ignore — keep last good payload
    }
  }, [token, eaId, myMemberId]);
  React.useEffect(() => { load(); }, [load]);

  // Pre-populate draft from any previously-saved row for `myMemberId`
  // so they can edit their own scores after saving (without re-entering).
  React.useEffect(() => {
    if (!myMemberId || !data) return;
    const me = (data.members || []).find((m) => m.member_id === myMemberId);
    if (me) {
      setDraft(me.scores || {});
      setComment(me.comment || '');
    } else {
      setDraft({});
      setComment('');
    }
  }, [myMemberId, data]);

  const pickMember = (mid) => {
    try { localStorage.setItem('viatris.member_id', mid); } catch { /* noop */ }
    setMyMemberId(mid);
  };

  // ────────────────────────────────────────────────────────────────
  // Auto-save on dropdown change (Feb 2026) — mirrors the ReviewCard
  // behaviour so the modal Scoring Matrix also flips the asset to
  // "Under Review" the moment ANY dim is picked, not just on explicit
  // Submit. Debounced 900 ms.
  //
  // (Feb 2026 hot-fix — refresh loop) Same root cause as ReviewCard:
  //   1. We DO NOT call `load()` after a successful save. Re-fetching
  //      committee state from the server pushed a new prop tree down
  //      into ScoringMatrix, which reset local `draft`, which re-
  //      triggered this effect — every ~700ms → an infinite loop.
  //   2. The auto-save chip is the reviewer-visible confirmation; the
  //      next explicit Submit or a re-open of the modal will pull
  //      fresh state from the server naturally.
  // ────────────────────────────────────────────────────────────────
  const [autoSaveState, setAutoSaveState] = React.useState('idle');
  const lastAutoSavedRef = React.useRef('');
  React.useEffect(() => {
    if (!myMemberId) return;
    const hasAny = SCORING_DIMS.some((d) => _hasAnswer(draft[d]));
    if (!hasAny) return;
    const key = JSON.stringify({ d: draft, c: comment });
    if (key === lastAutoSavedRef.current) return;
    const handle = setTimeout(async () => {
      setAutoSaveState('saving');
      try {
        try { localStorage.setItem('viatris.member_id_locked', '1'); } catch { /* noop */ }
        await axios.post(
          `${API}/client/viatris/${token}/assets/${eaId}/member-scores`,
          { member_id: myMemberId, scores: draft, comment },
        );
        lastAutoSavedRef.current = key;
        setAutoSaveState('saved');
        // Deliberately NOT calling `load()` here — see hot-fix comment.
        setTimeout(() => setAutoSaveState((s) => s === 'saved' ? 'idle' : s), 2400);
      } catch {
        setAutoSaveState('idle');
      }
    }, 900);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, comment, myMemberId]);
  const switchMember = () => {
    try { localStorage.removeItem('viatris.member_id'); } catch { /* noop */ }
    setMyMemberId(null);
    setDraft({});
    setComment('');
  };

  const memberHasSubmitted = (mid) => {
    const m = (data?.members || []).find((m) => m.member_id === mid);
    if (!m) return false;
    return SCORING_DIMS.some((d) => _hasAnswer(m.scores?.[d]));
  };
  const mySubmitted = !!myMemberId && memberHasSubmitted(myMemberId);
  const anyDimsInDraft = SCORING_DIMS.some((d) => _hasAnswer(draft[d]));

  const save = async () => {
    if (!myMemberId) return;
    if (!anyDimsInDraft) {
      alert('Please score at least one domain before submitting.');
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/client/viatris/${token}/assets/${eaId}/member-scores`,
        { member_id: myMemberId, scores: draft, comment },
      );
      await load();
    } catch (e) {
      alert('Save failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const memberScore = (mid, dim) => {
    const m = (data?.members || []).find((m) => m.member_id === mid);
    return m?.scores?.[dim];
  };
  const aiScore = (dim) => {
    const v = aiScoring?.[dim];
    return v && typeof v === 'object' ? v.score : null;
  };

  // Identity gate — until they declare who they are, no scoring UI.
  if (!myMemberId) {
    return (
      <div className="space-y-4">
        <div className="text-xs text-slate-700 leading-relaxed bg-indigo-50/60 border border-indigo-200 rounded p-3" data-testid="member-blinding-gate">
          <div className="font-semibold text-indigo-800 mb-1">Blinded committee scoring</div>
          <p className="text-[11px] text-slate-700">
            To preserve independent judgment, each reviewer scores in <strong>isolation</strong>.
            Select the role you are reviewing as to begin. You will see only the ForesightRx
            AI score while you enter yours. The other reviewers&rsquo; scores and the
            committee average become visible only after you submit at least one
            score of your own — partial submissions are welcome.
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="pick-member-grid">
          {REVIEWER_ROLES.map((role) => {
            const submitted = memberHasSubmitted(role.id);
            return (
              <button
                key={role.id}
                onClick={() => pickMember(role.id)}
                className={`border rounded-lg px-3 py-3 text-left transition-all hover:shadow-sm ${
                  submitted ? 'border-emerald-300 bg-emerald-50/40' : 'border-slate-200 bg-white hover:border-indigo-300'
                }`}
                data-testid={`pick-${role.id}`}
              >
                <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">{role.label}</div>
                <div className="text-[11px] text-slate-700 mt-0.5">
                  {submitted ? '✓ Already scored' : 'Not yet scored'}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Once identity is picked: render the table with blinding rules.
  const myLabel = ROLE_LABEL[myMemberId] || myMemberId;
  const myShort = ROLE_SHORT[myMemberId] || myMemberId;
  return (
    <div className="space-y-3" data-testid="committee-scoring-table">
      {/* Identity strip */}
      <div className="flex items-center justify-between gap-3 flex-wrap bg-indigo-50/60 border border-indigo-200 rounded p-2.5">
        <div className="text-xs text-slate-700">
          You are scoring as <strong className="text-indigo-700">{myLabel}</strong>.
          {mySubmitted
            ? <span className="ml-1 text-emerald-700">Your scores are in — full committee view is now unlocked.</span>
            : <span className="ml-1 text-amber-700">Blinded mode — other reviewers&rsquo; scores are hidden until you submit all {SCORING_DIMS.length} domains.</span>}
        </div>
        <div className="flex items-center gap-2">
          {autoSaveState === 'saving' && (
            <span className="text-[10px] inline-flex items-center gap-1 text-amber-700" data-testid="matrix-autosave-saving">
              <Loader2 className="w-2.5 h-2.5 animate-spin" />
              Saving…
            </span>
          )}
          {autoSaveState === 'saved' && (
            <span className="text-[10px] inline-flex items-center gap-1 text-emerald-700" data-testid="matrix-autosave-saved">
              <Check className="w-2.5 h-2.5" />
              Saved · Under Review
            </span>
          )}
          <button
            onClick={switchMember}
            className="text-[11px] text-indigo-600 hover:underline"
            data-testid="switch-member"
          >
            Change member
          </button>
        </div>
      </div>

      {/* Submission progress */}
      <div className="text-xs text-slate-600">
        <strong>{data?.members_submitted ?? 0} of 6 members</strong> have submitted this asset
        {data?.members_full_scored != null && data.members_full_scored < (data?.members_submitted ?? 0)
          ? ` · ${data.members_full_scored} scored every dim`
          : ''}.
        {data?.derived_status && (
          <Badge className={`ml-2 text-[10px] ${DECISION_BADGE[data.derived_status] || ''}`}>
            {data.derived_status}
            {data.committee_overall_avg != null && (data?.members_submitted ?? 0) >= 6
              ? ` · ${data.committee_overall_avg.toFixed(2)}/5`
              : ''}
          </Badge>
        )}
        {data?.decision_reason && (
          <div className="text-[10px] text-slate-500 mt-0.5">{data.decision_reason}</div>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse [&_th]:border [&_th]:border-slate-200 [&_td]:border [&_td]:border-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-2 py-1.5 text-left text-[10px] uppercase tracking-wide text-slate-500">Domain</th>
              <th className="px-2 py-1.5 text-center text-[10px] uppercase tracking-wide text-indigo-700">AI</th>
              {MEMBER_IDS.map((mid) => {
                const isMe = mid === myMemberId;
                // Blinding rule: hide other reviewers' columns until "me"
                // has fully submitted. My column always visible.
                if (!isMe && !mySubmitted) return null;
                const sh = ROLE_SHORT[mid] || mid;
                const lbl = ROLE_LABEL[mid] || mid;
                return (
                  <th key={mid}
                      title={lbl}
                      className={`px-2 py-1.5 text-center text-[10px] uppercase tracking-wide ${isMe ? 'text-indigo-700 bg-indigo-50' : 'text-slate-600'}`}>
                    {sh}{isMe ? ' (you)' : ''}
                  </th>
                );
              })}
              {mySubmitted && (
                <>
                  <th className="px-2 py-1.5 text-center text-[10px] uppercase tracking-wide text-emerald-700">Avg</th>
                  <th className="px-2 py-1.5 text-center text-[10px] uppercase tracking-wide text-slate-500" title="Min / Max across reviewers">Min–Max</th>
                  <th className="px-2 py-1.5 text-center text-[10px] uppercase tracking-wide text-slate-500" title="Standard deviation across reviewers — lower = stronger consensus">σ</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {SCORING_DIMS.map((dim, dimIdx) => {
              const stats = (data?.consensus_per_dim || {})[dim] || {};
              return (
              <tr key={dim} className="border-b border-slate-100">
                <td className="px-2 py-1.5 text-slate-700">
                  <span className="text-[10px] font-mono text-slate-400 tabular-nums mr-1.5">{dimIdx + 1}.</span>
                  {(data?.scoring_dim_labels || DIM_LABELS)[dim] || dim.replace(/_/g, ' ')}
                </td>
                <td className="px-2 py-1.5 text-center font-mono text-indigo-700">{aiScore(dim) ?? '—'}</td>
                {MEMBER_IDS.map((mid) => {
                  const isMe = mid === myMemberId;
                  if (!isMe && !mySubmitted) return null;
                  if (isMe) {
                    return (
                      <td key={mid} className="px-2 py-1.5 text-center bg-indigo-50/40">
                        <select
                          value={draft[dim] ?? ''}
                          onChange={(e) => setDraft({ ...draft, [dim]: _parseScoreInput(e.target.value) })}
                          className="w-16 text-xs border border-indigo-300 rounded px-0.5 py-0.5 bg-white"
                          data-testid={`score-input-${myMemberId}-${dim}`}
                        >
                          <option value="">—</option>
                          <option value="skip">Skip</option>
                          {[1,2,3,4,5].map((n) => <option key={n} value={n}>{n}</option>)}
                        </select>
                      </td>
                    );
                  }
                  return (
                    <td key={mid} className="px-2 py-1.5 text-center">
                      {(() => {
                        const v = memberScore(mid, dim);
                        if (_isSkip(v)) return <span className="inline-block text-[10px] font-semibold text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">SKIP</span>;
                        return <span className="font-mono text-slate-700">{v ?? '—'}</span>;
                      })()}
                    </td>
                  );
                })}
                {mySubmitted && (
                  <>
                    <td className="px-2 py-1.5 text-center font-mono font-semibold text-emerald-700">
                      {data?.committee_avg_per_dim?.[dim] ?? '—'}
                    </td>
                    <td className="px-2 py-1.5 text-center font-mono text-slate-600">
                      {stats.min != null && stats.max != null ? `${stats.min}–${stats.max}` : '—'}
                    </td>
                    <td className="px-2 py-1.5 text-center font-mono text-slate-600">
                      {stats.stddev != null ? stats.stddev.toFixed(2) : '—'}
                    </td>
                  </>
                )}
              </tr>
            );})}
            <tr className="bg-slate-50 font-semibold">
              <td className="px-2 py-2 text-slate-800">Overall (avg of domains)</td>
              <td className="px-2 py-2 text-center font-mono text-indigo-700">
                {aiScoring ? (Object.values(aiScoring).filter(v => v?.score).reduce((a, v) => a + v.score, 0) / Math.max(1, Object.values(aiScoring).filter(v => v?.score).length)).toFixed(2) : '—'}
              </td>
              {MEMBER_IDS.map((mid) => {
                const isMe = mid === myMemberId;
                if (!isMe && !mySubmitted) return null;
                const N = SCORING_DIMS.length;
                if (isMe) {
                  const myVals = SCORING_DIMS.map((d) => draft[d]).filter((v) => typeof v === 'number');
                  const myAvg = myVals.length === N ? (myVals.reduce((a, b) => a + b, 0) / N).toFixed(2) : '—';
                  return <td key={mid} className="px-2 py-2 text-center font-mono text-indigo-700 bg-indigo-50/60">{myAvg}</td>;
                }
                const m = (data?.members || []).find((m) => m.member_id === mid);
                const mScores = m?.scores || {};
                const vals = SCORING_DIMS.map((d) => mScores[d]).filter((v) => typeof v === 'number');
                const avg = vals.length === N ? (vals.reduce((a, b) => a + b, 0) / N).toFixed(2) : '—';
                return <td key={mid} className="px-2 py-2 text-center font-mono text-slate-700">{avg}</td>;
              })}
              {mySubmitted && (
                <>
                  <td className="px-2 py-2 text-center font-mono text-emerald-700">
                    {data?.committee_overall_avg ?? '—'}
                  </td>
                  <td className="px-2 py-2 text-center font-mono text-slate-600">
                    {data?.consensus_overall?.min != null && data?.consensus_overall?.max != null
                      ? `${data.consensus_overall.min}–${data.consensus_overall.max}` : '—'}
                  </td>
                  <td className="px-2 py-2 text-center font-mono text-slate-600">
                    {data?.consensus_overall?.stddev != null ? data.consensus_overall.stddev.toFixed(2) : '—'}
                  </td>
                </>
              )}
            </tr>
            {/* Committee Weighted Avg row — per-reviewer weighted mean over the dims they scored */}
            <tr className="bg-indigo-50 font-bold border-t-2 border-indigo-200">
              <td className="px-2 py-2 text-indigo-900">Committee Weighted Avg (/ 5)</td>
              <td className="px-2 py-2 text-center font-mono text-indigo-800">
                {aiScoring
                  ? (() => {
                      const vs = Object.values(aiScoring).filter(v => typeof v?.score === 'number').map(v => v.score);
                      return vs.length ? (vs.reduce((a, b) => a + b, 0) / vs.length).toFixed(2) : '—';
                    })()
                  : '—'}
              </td>
              {MEMBER_IDS.map((mid) => {
                const isMe = mid === myMemberId;
                if (!isMe && !mySubmitted) return null;
                if (isMe) {
                  const myVals = SCORING_DIMS.map((d) => draft[d]).filter((v) => typeof v === 'number');
                  const wavg = myVals.length ? (myVals.reduce((a, b) => a + b, 0) / myVals.length).toFixed(2) : '—';
                  return <td key={mid} className="px-2 py-2 text-center font-mono text-indigo-800 bg-indigo-100">{wavg}</td>;
                }
                const m = (data?.members || []).find((m) => m.member_id === mid);
                const vals = SCORING_DIMS.map((d) => (m?.scores || {})[d]).filter((v) => typeof v === 'number');
                const wavg = vals.length ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(2) : '—';
                return <td key={mid} className="px-2 py-2 text-center font-mono text-indigo-800">{wavg}</td>;
              })}
              {mySubmitted && (
                <td colSpan={3} className="px-2 py-2 text-center font-mono text-indigo-800">
                  {data?.committee_overall_avg != null
                    ? data.committee_overall_avg.toFixed(2)
                    : '—'}
                </td>
              )}
            </tr>
          </tbody>
        </table>
      </div>

      {/* My submission panel — always visible to me */}
      <div className="space-y-2 p-3 bg-indigo-50/50 border border-indigo-200 rounded">
        <div className="text-[11px] text-slate-700">
          {mySubmitted
            ? <>Your scores are saved. You can revise any domain and resubmit at any time.</>
            : <>
                Score every domain you can, on a 1 (weak) to 5 (excellent) scale. Use <strong>Skip</strong> for any domain you can&rsquo;t confidently assess — skips are excluded from the committee average, not counted against you.
                <span className="ml-1 text-slate-500">{Object.values(draft).filter(_hasAnswer).length} of {SCORING_DIMS.length} domains answered.</span>
              </>}
        </div>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Optional rationale for your scoring…"
          rows={2}
          className="w-full text-xs border border-slate-200 rounded p-2 focus:border-indigo-400 focus:outline-none"
          data-testid="member-score-comment"
        />
        <Button
          size="sm"
          onClick={save}
          disabled={saving || !anyDimsInDraft}
          className="bg-indigo-600 hover:bg-indigo-700 text-white h-7 text-[11px] disabled:opacity-50"
          data-testid="save-member-score"
        >
          {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
          {mySubmitted ? `Re-submit ${myShort}'s scores` : `Submit ${myShort}'s scores`}
        </Button>
      </div>

      {/* Reviewer rationales — only visible after I've submitted */}
      {mySubmitted && (data?.members || []).filter((m) => m.comment).length > 0 && (
        <div className="pt-2 border-t border-slate-100 space-y-1">
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Reviewer rationales</div>
          {(data?.members || []).filter((m) => m.comment).map((m) => (
            <div key={m.member_id} className="text-[11px] text-slate-700">
              <strong>{ROLE_LABEL[m.member_id] || m.member_id}{m.member_id === myMemberId ? ' (you)' : ''}:</strong> &ldquo;{m.comment}&rdquo;
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function DeepDiveAlerts({ alerts }) {
  if (!alerts || !alerts.length) {
    return (
      <div className="text-slate-500 text-sm">
        No alerts in the last 90 days for this company.
      </div>
    );
  }
  const SEVERITY_BADGE = {
    Critical: 'bg-rose-100 text-rose-900 border-rose-300',
    High:     'bg-orange-100 text-orange-800 border-orange-300',
    Medium:   'bg-amber-100 text-amber-800 border-amber-300',
    Low:      'bg-sky-100 text-sky-800 border-sky-300',
  };
  return (
    <div className="space-y-2">
      <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold inline-flex items-center gap-1">
        {alerts.length} alert{alerts.length === 1 ? '' : 's'} · last 90 days
        <DefinitionInfo defKey="latest_alerts" />
      </div>
      {alerts.map((a) => (
        <div key={a.id} className="border border-slate-100 rounded p-3 hover:border-indigo-200 transition-colors">
          <div className="flex items-center gap-1.5 flex-wrap mb-1">
            <Badge className={`text-[10px] border ${SEVERITY_BADGE[a.severity] || 'bg-slate-100 text-slate-700'}`}>
              {a.severity || 'Medium'}
            </Badge>
            {a.category && (
              <Badge variant="outline" className="text-[10px] capitalize">{a.category}</Badge>
            )}
            <span className="text-[10px] text-slate-500">
              {(a.detected_at || '').slice(0, 10)}
            </span>
          </div>
          <div className="text-sm font-medium text-slate-800">{a.headline}</div>
          {a.summary && (
            <p className="text-xs text-slate-600 leading-relaxed mt-1">{a.summary}</p>
          )}
          {a.source_url && (
            <a href={a.source_url} target="_blank" rel="noreferrer" className="text-[11px] text-indigo-700 hover:underline mt-1 inline-block break-all">
              Source ↗
            </a>
          )}
        </div>
      ))}
    </div>
  );
}



// DeepDiveRefs / CitedText / SectionRefStrip / ReferenceRail moved
// to /app/frontend/src/lib/deepDiveRender.js on Feb 2026 so the
// Cohort and Reference Library modals share one canonical implementation.
